import { TableFilterColumns } from "./../utils/spreadsheet-columns.service";
import { PerimeterReferenceDataMap } from "../utils/reference-data.service";
import { ColDef, ColGroupDef, IServerSideGetRowsRequest } from "ag-grid-community";
import { marker as _ } from "@colsen1991/ngx-translate-extract-marker";
import { TypeOfUse } from "../utils/emission-factor.service";

export const GROUP_PERMISSION_ACCESS_MEMBERS_GROUP = _("access_members_group");
export const GROUP_PERMISSION_EDIT_MEMBERS_GROUP = _("edit_members_group");
export const GROUP_PERMISSION_EDIT_TEAM_MEMBER = _("edit_team_member");
export const GROUP_PERMISSION_EDIT_SCENARIO = _("edit_scenario");
export const GROUP_PERMISSION_EDIT_NOTATION_WEIGHT = _("edit_notation_weight");
export const GROUP_PERMISSION_EDIT_QUOTE_PART = _("edit_quote_part");
export const GROUP_PERMISSION_EDIT_ENERGY_SETTINGS = _("edit_energy_settings");
export const GROUP_PERMISSION_EDIT_ENERGY_CONSUMPTION = _("edit_energy_consumption");
export const GROUP_PERMISSION_EDIT_REFRIGERANT_FLUID = _("edit_refrigerant_fluid");
export const GROUP_PERMISSION_EDIT_FLASH_BUDGET = _("can_edit_flash_budget");
export const GROUP_PERMISSION_EDIT_INVESTMENT_TAG = _("edit_investment_tag");
export const GROUP_PERMISSION_EDIT_INITIATIVE_TAG = _("edit_initiative_tag");
export const GROUP_PERMISSION_EDIT_SITE = _("edit_site");
export const GROUP_PERMISSION_EDIT_REFERENCE_FIELD = _("edit_reference_field");
export const GROUP_PERMISSION_EDIT_REFERENCE_DATA = _("edit_reference_data");
export const GROUP_PERMISSION_EDIT_PRICE_SHEET = _("edit_price_sheet");
export const GROUP_PERMISSION_ACCESS_AUDIT = _("access_audit");
export const GROUP_PERMISSION_EDIT_DOCUMENT_TYPE = _("edit_document_type");
export const GROUP_PERMISSION_ACCESS_DOCUMENT_TYPE = _("access_document_type");
export const GROUP_PERMISSION_ACCESS_GENERIC_GAMME = _("access_generic_gamme");
export const GROUP_PERMISSION_ACCESS_EQUIPMENT_VALIDATION = _("access_equipment_validation");
export const GROUP_PERMISSION_ACCESS_ROADMAP_INVENTORY = _("access_roadmap_inventory");
export const GROUP_PERMISSION_EDIT_FIDJI_ACT_PRIORITY = _("edit_fidji_act_priority");
export const GROUP_PERMISSION_EDIT_GAMME_SITE = _("edit_gamme_site");
export const GROUP_PERMISSIONS = [
  GROUP_PERMISSION_EDIT_FLASH_BUDGET,
  GROUP_PERMISSION_EDIT_INVESTMENT_TAG,
  GROUP_PERMISSION_EDIT_INITIATIVE_TAG,
  GROUP_PERMISSION_EDIT_SITE,
  GROUP_PERMISSION_EDIT_REFERENCE_FIELD,
  GROUP_PERMISSION_EDIT_REFERENCE_DATA,
  GROUP_PERMISSION_ACCESS_MEMBERS_GROUP,
  GROUP_PERMISSION_EDIT_MEMBERS_GROUP,
  GROUP_PERMISSION_EDIT_TEAM_MEMBER,
  GROUP_PERMISSION_EDIT_SCENARIO,
  GROUP_PERMISSION_EDIT_NOTATION_WEIGHT,
  GROUP_PERMISSION_EDIT_ENERGY_SETTINGS,
  GROUP_PERMISSION_EDIT_ENERGY_CONSUMPTION,
  GROUP_PERMISSION_EDIT_REFRIGERANT_FLUID,
  GROUP_PERMISSION_EDIT_PRICE_SHEET,
  GROUP_PERMISSION_EDIT_QUOTE_PART,
  GROUP_PERMISSION_ACCESS_AUDIT,
  GROUP_PERMISSION_EDIT_DOCUMENT_TYPE,
  GROUP_PERMISSION_ACCESS_DOCUMENT_TYPE,
  GROUP_PERMISSION_ACCESS_GENERIC_GAMME,
  GROUP_PERMISSION_ACCESS_EQUIPMENT_VALIDATION,
  GROUP_PERMISSION_ACCESS_ROADMAP_INVENTORY,
  GROUP_PERMISSION_EDIT_FIDJI_ACT_PRIORITY,
  GROUP_PERMISSION_EDIT_GAMME_SITE,
] as const;

export interface IdNameMixin {
  id: number;
  name: string;
}

export interface LastChangeMixin {
  /** Date of the last change. */
  last_change_on: Date;
  /** Id. of the `User` who did the change last. */
  last_change_by: number;
}

export interface ImportableMixin<T> {
  imported_at: string;
  import_source: string;
  import_reference: string;
  import_statuses: T[];
}

interface MappedImportableMixin {
  // Dispalying directly the dates then applying a renderer to format it doesn't work well enough
  // Indeed the dates displayed in the filter are not formatted so I format them before
  imported_at_str: string;
  import_statuses_str: string;
}

interface OrderingMixin {
  ordering: number;
}

interface IsDefaultMixin {
  is_default: boolean;
}

interface AvailabilityMixin {
  available_from: number;
  available_to: number;
}

export interface LoadingState<T> {
  loading: boolean;
  error?: Error | null;
  data?: T;
}

export type RequireAtLeastOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Pick<T, Exclude<Keys, K>>>;
  }[Keys];

export type RequireOnlyOne<T, Keys extends keyof T = keyof T> = Pick<T, Exclude<keyof T, Keys>> &
  {
    [K in Keys]-?: Required<Pick<T, K>> & Partial<Record<Exclude<Keys, K>, undefined>>;
  }[Keys];

/**
 * Models of the KPI application.
 */
export namespace KPI {
  /**
   * Global backend configuration.
   */
  export interface Config {
    /** Currency symbol. */
    currency: string;
    notation_alpha_coefficient: number;
    margin_consistency_technical_state_lifetime: string | number;
    quote_part_enabled: boolean;
    investmentFullNomenclature: boolean;
  }

  type MenuKey = string; // list of MENU_KEY can be found in the SYSTEM__MAIN_MENU setting, for i.e: "ADMIN", "SITE", "ASSET", etc.

  export interface Display<T> {
    id: T;
    name: string;
  }

  export interface Corporate extends IdNameMixin {}

  export interface UserTeamMemberInput {
    first_name: string;
    last_name?: string;
    corporate: number;
    // decision menu permissions
    can_access_sites?: boolean;
    can_access_insight?: boolean;
    can_access_decision?: boolean;
    can_access_energy?: boolean;
    can_access_import?: boolean;
    can_access_workflow?: boolean;
    // decision other permissions
    can_access_report?: boolean;
    can_lead_workflow?: boolean;
  }

  export interface UserInput {
    first_name: string;
    last_name?: string;
    email: string;
    password?: string;
    // set user to 1 MemberGroup (at the moment, we only allow 1 user 1 group)
    group_id?: number;
    teammember: Partial<UserTeamMemberInput>;
  }

  /**
   * User having access to the application.
   */
  export interface User {
    /** Id. of the TeamMember. */
    id: number;
    /** Id. of the User. */
    get_user_id: number;
    email: string;
    name: string;
    username: string;
    first_name: string;
    last_name: string;
    uuid: string;
    /** Id. of the user `Corporate`. */
    corporate: number;
    /** Name of the user `Corporate`. */
    corporate_name: string;
    can_access_sites: boolean;
    can_access_insight: boolean;
    can_access_decision: boolean;
    can_access_energy: boolean;
    can_access_reconciliation: boolean;
    can_access_management: boolean;
    can_access_administration: boolean;
    can_access_report: boolean;
    can_access_opex: boolean;
    can_access_import: boolean;
    can_access_workflow: boolean;
    can_lead_workflow: boolean;
    workflow_chat_url: string;
    perimeter_permissions: PerimeterPermission[];
    last_active?: string | null;
  }

  export type GroupPermissionCode = `delete_${string}` | `view_${string}` | `add_${string}` | `change_${string}`;

  export interface GroupPermission {
    name: string;
    verbose_name: string;
    permissions: GroupPermissionCode[];
  }


  export interface DeprecatedGroupPermission {
    id: number;
    setup_reference: AvailableGroupPermission;
    show_in_admin: boolean;
    used_on_fidji_platform: boolean;
  }

  export type AvailableGroupPermission = (typeof GROUP_PERMISSIONS)[number];

  /**
   * Regroups `TeamMember` and defined some rights for this members
   * In particular what are the allowed investment status transitions for the members in the group
   */
  export interface MembersGroup extends IdNameMixin {
    /** Ids. of `TeamMember`s in the group */
    members: number[];
    /** List of allowed investment status transitions for this members group */
    allowed_investment_status_transitions: InvestmentStatusTransition[];
    permissions: DeprecatedGroupPermission[];
    report_template_permissions: number[];
    setup_reference?: string;
    is_superadmin?: boolean;
    can_access_audit?: boolean;
    scenario_management_enabled?: boolean;
    group_permissions: GroupPermissionCode[];
    menu_accesses: MenuKey[];
  }

  export interface MembersGroupInput extends IdNameMixin {
    members: number[];
    allowed_investment_status_transitions: number[];
    permissions: number[];
    report_template_permissions: number[];
    can_access_audit: boolean;
    scenario_management_enabled: boolean;
    menu_accesses: MenuKey[];
  }

  /** Same as `MembersGroup` but for the current `User` */
  export interface AllowedStatusTransition {
    /** The AllowedStatusTransition id */
    id: number;
    /** The allowed status from id */
    from_status: number;
    /** The allowed status from id */
    to_status: number;
  }

  export interface PerimeterPermission {
    id: number;
    perimeter: number;
    benchmark_only: boolean;
    user: number;
    read_only: boolean;
  }

  export interface Default {
    asset: number;
    comment: string;
    datetime: string;
    kpi_expert_name: string;
    notation_pictures: Picture[];
    note: number;
    order: number;
    user: User;
  }

  /**
   * Generic picture
   */
  export interface Picture {
    id: number;
    datetime: string;
    author: string;
    local_id: string;
    picture: string;
    thumbnail: string;
  }

  export interface ControlPoint {
    audit: number;
    audit_type: number;
    chapter: number;
    id: number;
    perimeter: number;
    status: number;
    last_change_on: string;
    last_change_by: number;
    pause_comment: string;
    themes: { id: number; name: string }[];
  }

  export interface ControlPointsByPerimeter {
    perimeter: number;
    totalControlPoints: number;
  }

  export interface ControlPointsByPerimeterAndTheme {
    perimeter: number;
    totalControlPoints: {
      theme: number;
      total: number;
    }[];
  }

  /**
   * State of a `Task`.
   */
  export enum TaskState {
    STATE_PENDING,
    STATE_IN_PROGRESS,
    STATE_DONE,
    STATE_CANCELLED
  }

  /**
   * Due task for a `User`.
   */
  export interface Task {
    id: number;
    /** Local id. used for offline synchronization. */
    local_id?: string;
    label: string;
    description?: string;
    created_by: number;
    /** Id. of the assigned `User`. */
    assigned: number;
    state: TaskState;
    done: boolean;
    deadline: string;
    /** Id. of the related `Perimeter`. */
    perimeter?: number;
    /** Id. of the related `Investment`. */
    investment?: number;
    /** Id. of the related `ReferenceField`. */
    assigned_role?: number;
    /** Id. of the related `Asset`. */
    asset?: number;
  }

  /**
   * `Task` object with related objects attached.
   */
  export interface MappedTask extends Task {
    assigned_object: User;
    created_by_object: User;
    state_object: Display<TaskState>;
    deadline_date: Date;
    investment_object?: KPI.MappedInvestment;
    asset_object?: KPI.MappedAsset;
  }
  /**
   * Group of task
   */
  export interface GroupTasks extends MappedTask {
    /** Id. of the related `Investments`. */
    investments?: number[];
    /** Id. of the related `Assets`. */
    assets?: number[];
  }

  /**
   * Type of a `ReferenceField`.
   */
  export enum ReferenceFieldDataType {
    DATA_TYPE_STRING,
    DATA_TYPE_NUMBER,
    DATA_TYPE_FK,
    DATA_TYPE_USER
  }

  /**
   * Type of filtering of a `ReferenceField`.
   */
  export enum ReferenceFieldFilterType {
    FILTER_NO,
    FILTER_VALUE,
    FILTER_RANGE
  }

  /**
   * Possible values for a `ReferenceField` of type `DATA_TYPE_FK`.
   */
  export interface ReferenceDataValue {
    id: number;
    label: string;
  }

  /**
   * Type of a `PPA option`.
   */
  export enum PPAOption {
    LAST_AUDIT,
    CRITICAL,
    OUT_OF_ORDER
  }

  /**
   * Definition of a reference data applying to a `Perimeter`.
   */
  export interface ReferenceField {
    id: number;
    var_name: string;
    label: string;
    data_type: ReferenceFieldDataType;
    order: number;
    filtering_audit: ReferenceFieldFilterType;
    filtering_intelligence: ReferenceFieldFilterType;
    show_sum_on_multi_perimeters: boolean;
    report_grouping_multi_perimeters: number;
    report_grouping_perimeters_set: number;
    displayed_audit: boolean;
    displayed_intelligence: boolean;
    values?: ReferenceDataValue[];
    only_for_perimeter_types: number[];
    available_for_sites: boolean;
    is_locked: boolean;
  }

  /**
   * Definition of an `Audit`
   */
  export interface Audit {
    id: number;
    year: number;
    is_closed: boolean;
  }

  export interface NotationState {
    id: number;
    low: number;
    high: number;
  }

  /**
   * Groups of perimeters
   */
  export interface Cluster extends IdNameMixin {
    order: number;
  }

  /**
   * Type of `Perimeters`
   */
  export interface PerimeterType extends IdNameMixin {
    order: number;
    level: number;
    perimeter_type_class: PerimeterTypeClass;
    only_for_clusters: number[];
  }

  export enum PerimeterTypeClass {
    CLASS_NONE,
    CLASS_GLOBAL,
    CLASS_BUILDING,
    CLASS_OTHER
  }

  /**
   * Picture of a `Perimeter`.
   */
  export interface PerimeterPicture extends Picture {
    /** Id. of the associate `Perimeter` */
    perimeter: number;
  }

  /**
   * Set of buildings.
   */
  export interface Perimeter extends IdNameMixin {
    read_only: boolean;
    building_id: number;
    is_monosite: boolean;
    level: number;
    level_parent?: number;
    order: number;
    perimeter_type: number;
    perimeter_type_object?: PerimeterType;
    cluster?: number;
    cluster_object?: Cluster;
    ref_data: PerimeterReferenceValue[];
    sub_perimeters?: Perimeter[];
    _filter_ref_data?: PerimeterReferenceValue[];
    pictures?: PerimeterPicture[];
    cover_image?: number;
    parent?: Perimeter;
    coordinates?: string;
    flooring_area?: number;
    creation_year?: number;
    baseline_year?: number;
    site_notes?: string;
    code?: string;
    levelParentPerimeterType?: number;
  }

  /**
   * Manage a budget closing date for a perimeter
   */
  export interface PerimeterBudgetPlan {
    id: number;
    /** Id. of associated `Perimeter` */
    perimeter: number;
    /** Id. of associated `Audit` */
    audit: number;
    close_date: string;
    close_date_obj?: Date;
    is_closed: boolean;
    year: number;
  }

  /**
   * Reference information on a `Perimeter`.
   */
  export interface PerimeterReferenceValue {
    id: number;
    /** Id. of the `ReferenceField` */
    field: number;
    /** Id. of the `Perimeter` */
    perimeter: number;
    year: number;
    label: string;
    var_name: string;
    value: string | number;
    filter_value: number;
    aggregated: boolean;

    /** useful for the creation and the update */
    string_value?: string;
    decimal_value?: string | number;
    fk_value?: number;
    user_value?: number;

    date: string;
    source: string;
    author: number;
  }

  /**
   * `DocumentType` requirement for a `Perimeter`.
   */
  export interface DocumentTypePerimeterRequirement {
    perimeter: number;
    mandatory: boolean;
  }

  /**
   * `DocumentType` requirement for an `InvestmentType`.
   */
  export interface DocumentTypeInvestmentTypeRequirement {
    investment_type: number;
    mandatory: boolean;
  }

  /**
   * `DocumentType` requirement for an `AssetType`.
   */
  export interface DocumentTypeAssetTypeRequirement {
    asset_type: number;
    mandatory: boolean;
  }

  /**
   * `DocumentType` requirement for an `InitiativeType`.
   */
  export interface DocumentTypeInitiativeTypeRequirement {
    initiative_type: number;
    mandatory: boolean;
  }

  /**
   * Type of a document attached to some `Perimeter`.
   */
  export interface DocumentType {
    id: number;
    label: string;
    order: number;
    is_maintenance_contract: boolean;
    perimeter_requirements: DocumentTypePerimeterRequirement[];
    asset_type_requirements: DocumentTypeAssetTypeRequirement[];
    investment_type_requirements: DocumentTypeInvestmentTypeRequirement[];
    initiative_type_requirements: DocumentTypeInitiativeTypeRequirement[];
  }

  export interface DocumentRequirement {
    perimeter: IdNameMixin;
    investment: Investment;
    investment_type: InvestmentType;
    asset: Asset;
    assetType: AssetType;
    document_type: DocumentType;
    mandatory: boolean;
    documents: Document[];
    suggestAutomaticReminder: boolean;
  }

  export interface DocumentTypeRelationship {
    id: number;
    document_type: number;
    content_type: number;
    content_type_label: string;
    object_id: number;
    values: { mandatory?: boolean; [key: string]: any };
  }

  export interface DocumentTypeRelationshipMatrix {
    document_types: DocumentType[];
    asset_type_hierarchy: any[];
    requirements: { document_type_id: number; asset_type_id: number; mandatory: boolean }[];
    content_type_id: number;
  }

  /**
   * Type of an `AuditQuestion`.
   */
  export enum AuditQuestionType {
    QUESTION_STARS = "stars",
    QUESTION_SLIDERS = "sliders",
    QUESTION_ON_OFF = "on-off"
  }

  /**
   * Yearly version of an `AuditQuestion`.
   */
  export interface AuditQuestionItem {
    id: number;
    year_number?: number;
  }

  /**
   * Question asked when a user evaluates an `Asset`.
   */
  export interface AuditQuestion {
    id: number;
    text: string;
    question_type: AuditQuestionType;
    help_text_subject: string;
    help_text: string;
    low_value_label: string;
    high_value_label: string;
    icon: string;
    items: AuditQuestionItem[];
    level: number;
    mnemonic: string;
    shown_in_report: boolean;
  }

  export interface KPIExpertSection {
    section_name: string;
    notations: KPIExpert[];
    show_chart_in_reports: boolean;
    order: number;
  }

  export interface KPIExpert {
    asset?: number;
    comment: string;
    kpi_expert_name: string;
    note?: number;
    notation_pictures: Picture[];
    maxPhotos?: number;
    order: number;
  }

  export interface PPAByCluster {
    [cluster: number]: {
      planned: PPA;
      hypothesis: PPA;
    };
  }

  export interface AssetsRenewalsChartData {
    [year: number]: {
      with_planned: number;
      with_unplanned: number;
      without: number;
    };
  }

  export interface KPIExpertChart {
    [category: number]: {
      name: string;
      total_assets: number;
      total_defaults: number;
      [expert_kpis: number]: {
        name: string;
        defaults_number: number;
      };
    };
  }

  export interface EmissionCategory extends IdNameMixin {
    scope: number;
    is_calculated: boolean;
  }

  export interface GHGEmissionsChart {
    emission_categories: EmissionCategory[];
    initiatives: {
      [emission_category_id: number]: {
        [year: number]: number;
      };
    };
  }

  export interface TrajectorySavingsByEnergyType extends IdNameMixin {
    color: string;
    number_of_investment: number;
    investment_price: number;
    number_of_initiative: number;
    savings_first_year: number;
    savings_full_year: number;
  }

  export interface TrajectoryInvestmentByStatus extends IdNameMixin {
    color: string;
    number_of_investment: number;
    investment_price: number;
  }

  export interface EnergyTrajectory {
    trajectory_points: EnergyTrajectoryPoint[];
  }

  export interface EnergyTrajectoryPoint {
    year: number;
    area: number;
    energy_consumption: number;
    sites_count: number;
    economy_marginal_in_consumption: number;
    is_extrapolated: boolean;
    investments_by_year_and_status: TrajectoryInvestmentByStatus[];
    contributions: {
      [energyTypeId: number]: number;
    };
  }

  export interface MinimalCO2Trajectory {
    year: number;
    ghg_emission: number;
    is_extrapolated: boolean;
    area: number;
  }
  export interface EnergyTrajectoryInitiative extends IdNameMixin {
    label: string;
    energy_type?: string;
    documents?: Document[];
    theoretical_savings_full_year?: number;
    theoretical_greenhouse_gas_savings_full_year?: number;
    investment_budget?: number;
    savings_start_year?: number;
    status?: string;
    site?: string;
    created_by?: string;
  }

  export interface MinimalEnergyTrajectory {
    year: number;
    area: number;
    energy_consumption: number;
    objective: number;
    is_objective_declared?: boolean;
    is_extrapolated: boolean;
  }

  export interface InitiativeEnergyTrajectory {
    energy_consumption_trajectory: MinimalEnergyTrajectory[];
    ghg_emission_trajectory: MinimalCO2Trajectory[];
    initiative_savings: { [year: number]: number };
    initiative_ghg_savings: { [year: number]: number };
    initiatives_building_savings: { [year: number]: number };
    initiatives_building_ghg_savings: { [year: number]: number };
    initiatives_in_years: { [year: number]: number[] };
  }

  export interface ProjectEnergyTrajectory {
    energy_consumption_trajectory: MinimalEnergyTrajectory[];
    project_initiative_savings: { [year: number]: number };
    building_initiative_savings: { [year: number]: number };
  }

  export interface ScenarioInitiativeComparison {
    [scenario: number]: {
      initiatives: MinimalInitiativeWithSavings[];
    };
  }

  export interface BudgetByYearAndArea {
    area: number;
    investment_number: number;
    investment_budget: number;
  }

  export interface CarbonTrajectoryChartData {
    [year: number]: {
      carbonTrajectory: CarbonTrajectoryPoint;
      ghgIntensityPathway1: GHGIntensityPathway;
      ghgIntensityPathway2: GHGIntensityPathway;
      budget: BudgetByYearAndArea;
    };
  }

  export interface CarbonTrajectory {
    trajectory_points: CarbonTrajectoryPoint[];
    active_initiatives: EnergyTrajectoryInitiative[];
    active_initiatives_ids: number[];
    active_initiatives_budget: number;
    active_initiatives_invest_nb: number;
    last_ratio: number;
    future_ratio: number;
    building_area: {
      [year: string]: number;
    };
  }

  export interface CarbonTrajectoryPoint {
    year: number;
    emission: number;
    is_extrapolated: boolean;
    contribution: {
      direct: number;
      indirect: {
        [energyTypeId: number]: number;
      };
    };
  }

  export interface Pathway {
    country: string;
    year: number;
    type_of_use: string;
    provider: number;
  }

  export interface GHGIntensityPathway extends Pathway {
    ghg_intensity: number;
  }

  export interface EnergyIntensityPathway extends Pathway {
    energy_intensity: number;
  }

  export interface StrandedSites {
    objective: {
      yearlyObjective: number;
    };
    site: Perimeter;
    trajectory: {
      yearlyTrajectory: number;
    };
  }

  export interface EligibilityTaxonomy {
    expense: [
      {
        eligible: number;
        taxonomy: {
          activityNumber: string;
          color: string;
          name: string;
          objective: {
            id: number;
            name: string;
          };
          sector: {
            id: number;
            name: string;
          };
        };
      }
    ];
  }

  export interface EligibilityByTaxonomyCat {
    capex: EligibilityTaxonomy;
    opex: EligibilityTaxonomy;
  }

  export interface InvestmentsByTaxonomyEligibility {
    expense: [
      {
        eligible: number;
        total_planned: number;
        eligible_linked_initiative_energy: number;
        total_planned_linked_initiative_energy: number;
        year: number;
      }
    ];
  }

  export interface TotalInvestmentsByTaxonomyEligibility {
    capex: InvestmentsByTaxonomyEligibility;
    opex: InvestmentsByTaxonomyEligibility;
  }

  export interface GHGEmissionsCard {
    total: number;
  }

  export interface ConsumptionCard {
    lastRatio: number;
    futureRatio: number;
    stillToSave: number;
    stillToSavePercent: number;
    yearFrom?: number;
    yearTo?: number;
    initiativesNb?: number;
    initiativesBudget?: string;
    investNb?: number;
  }

  export interface Consumption {
    ratio: number;
    total: number;
    year: number;
    sitesCount?: number;
  }

  export interface CarbonConsumption {
    last_ratio: number;
    future_ratio: number;
    initiatives_total: number;
    investments_total: number;
    total_budget: number;
  }

  export interface InitiativeCashFlow {
    cashflow_inputs: { [year: number]: number };
    cashflow_outputs: { [year: number]: number };
    payback: number;
  }

  export interface BaseConfidenceOnFeasibility {
    active_investment_price?: number;
    all_investment_price?: number;
    active_economy_marginal: number;
    all_economy_marginal: number;
    number_of_active_investment?: number;
    number_of_all_investment?: number;
    number_of_all_initiatives?: number;
    project_ids?: number[];
  }

  export interface ConfidenceOnFeasibilityType extends BaseConfidenceOnFeasibility {
    level_2?: ConfidenceByInitiativeTypes;
  }

  export interface ConfidenceByInitiativeTypes {
    [initiative_type: string]: ConfidenceOnFeasibilityType;
  }

  export interface ConfidenceOnFeasibility extends BaseConfidenceOnFeasibility {
    by_initiative_type: ConfidenceByInitiativeTypes;
  }

  export interface InitiativeFeasibilityData {
    consumption_confidence: {
      quick_win: ConfidenceOnFeasibility;
      minor_renovation: ConfidenceOnFeasibility;
      major_renovation: ConfidenceOnFeasibility;
    };
    emission_confidence: {
      quick_win: ConfidenceOnFeasibility;
      minor_renovation: ConfidenceOnFeasibility;
      major_renovation: ConfidenceOnFeasibility;
    };
    latest_year_declaration: number;
    co2_emission_without_initiative: number;
    energy_consumption_without_initiative: number;
    area: number;
    objectives: {
      [year: number]: {
        value: number;
        declared: boolean;
      };
    };
    sbti_objectives: {
      sbti_15: number;
      sbti_2: number;
    };
    other_inactive_investment_price: number;
    other_all_investment_count: number;
    other_active_investment_price: number;
    other_active_investment_count: number;
  }

  export interface PPA {
    [year: number]: {
      [status: number]: {
        budget: number;
        invest_nb: number;
      };
    };
  }
  export interface TaxonomyCategory extends IdNameMixin {
    activity_number: string;
    objective: IdNameMixin;
    sector: IdNameMixin;
    color: string;
  }

  export interface TaxonomyObjective extends IdNameMixin {
    order: number;
    icon_name: string;
    is_default: boolean;
    is_true: boolean;
  }

  /**
   * Zone in which an `Asset` can be placed.
   */
  export interface Zone extends IdNameMixin {
    setup_reference?: string;
  }

  /**
   * Type of ownership of an `Asset`.
   */
  export interface OwnershipType extends IdNameMixin {
    order: number;
  }

  /**
   * Power source of an `Asset`.
   */
  export interface AssetPowerSource extends IdNameMixin {
    order: number;
  }

  /**
   * A unit of a quantity.
   */
  export interface QuantityUnit extends IdNameMixin {
    is_default: boolean;
    minimal_quantity: number;
    maximal_quantity: number;
    setup_reference: string;
  }

  /**
   * A workload budget.
   */
  export interface InvestmentBudgetReferenceWorkload {
    hourly_rate: string | number;
    hours_number: string | number;
  }

  /**
   * A budget reference.
   */
  export interface InvestmentBudgetReference {
    budget_price: string | number;
    hypothesis: string;
    material_price: string | number;
    additional_price: string | number;
    workloads: InvestmentBudgetReferenceWorkload[];
  }

  /**
   * Type of an `Audit`.
   */
  export enum AuditType {
    GLOBAL = 1,
    DETAILED = 2
  }
  /**
   * Super-category of an `AssetType`.
   */
  export interface SuperCategory extends IdNameMixin, AvailabilityMixin, OrderingMixin {
    audit_type: AuditType;
    setup_reference?: string | null;
  }

  /**
   * Category of an `AssetType`.
   */
  export interface Category extends IdNameMixin, AvailabilityMixin, OrderingMixin {
    /** Id. of the parent `SuperCategory` */
    parent: number;
    setup_reference?: string | null;
  }

  /**
   * Sub-category of an `AssetType`.
   */
  export interface SubCategory extends IdNameMixin, AvailabilityMixin, OrderingMixin {
    /** Id. of the parent `Category`. */
    parent: number;
    /** Used for global notations */
    theoretical_lifetime?: number;
    setup_reference?: string | null;
  }

  /**
   * Type of an `Asset`.
   */
  export interface AssetType extends IdNameMixin, AvailabilityMixin, OrderingMixin {
    /** Id. of the parent `SubCategory`.*/
    parent: number;
    setup_reference?: string | null;
    /** Id. of the `QuantityUnit`. */
    quantity_unit: number;
    expected_duration: number;
    only_these_zones: number[];
    default_ownership_type: number;
    budget_reference_data?: InvestmentBudgetReference;
    level: number;
    model_support?: boolean;
    brand_support?: boolean;
    power_source_support?: boolean;
    power_support?: boolean;
    alpha_coefficient: number;
    show_refrigerant_type?: boolean;
    friendly_name?: string;
    show_heat_transfer_coefficient_u?: boolean;
    show_thermal_resistance_coefficient_r?: boolean;
    manage_latest_energy_yield_declaration?: boolean;
    show_equipment_count?: boolean;
    planned_recycle: boolean;
    planned_resale: boolean;
    is_vital_organ?: boolean;
    show_maintenance_code?: boolean;
    inies_environmental_data_sheet?: string[];
  }

  export interface MappedAssetType extends AssetType {
    super_category: SuperCategory;
    category: Category;
    sub_category: SubCategory;
  }

  /**
   * Floor of an `Asset`.
   */
  export interface AssetFloor extends IdNameMixin {
    only_for_clusters: number[];
    setup_reference?: string;
  }

  /**
   * Type of `Refrigerant`
   */
  export interface RefrigerantType extends IdNameMixin {}

  /**
   * Levels for AssetTypes
   */
  export enum AssetTypeLevel {
    NONE = 0,
    COLLECTION = 1,
    UNIT = 2,
    COMPONENT = 3,
    COLLECTION_ITEM = 4
  }

  /**
   * Status of an `Asset`.
   */
  export enum AssetStatus {
    STATUS_VALIDATED = 1,
    STATUS_WAITING_FOR_VALIDATION = 2
  }

  /**
   * Import status of an `Asset`.
   */
  export enum ImportableAssetStatus {
    NOTATION = "NOTATION",
    INSTALLATION_YEAR = "INSTALLATION_YEAR",
    READY = "READY",
    VALIDATED = "VALIDATED"
  }

  /**
   * Equipment in a building.
   */
  export interface Asset extends ImportableMixin<ImportableAssetStatus> {
    id: number;
    label: string;
    /** Id. of the building. */
    building: number;
    /** Id. of the `Zone`. */
    zone: number;
    floor: number;
    zone_details: string;
    asset_type: number;
    duration_deviation: number;
    end_of_life?: number;
    theoretical_end_of_life?: number;
    installation_year: number;
    quantity: number;
    status: AssetStatus;
    ownership_type: number;
    comments: string;
    expected_lifetime: number;
    lifetime_vs_theorical?: number;
    maintenance_code: string;
    uniformat_code?: string;
    budget_reference_data?: InvestmentBudgetReference;
    nb_documents: number;
    nb_tasks: number;
    notation_missing_reason: number;
    notation_missing_comment: string;
    parent: number;
    pictures?: Picture[];
    initiatives?: MinimalInitiative[];
    replaced_by?: number;
    replacement_of?: number;
    equipment_count?: number;
    heat_transfer_coefficient_u?: IdNameMixin;
    thermal_resistance_coefficient_r?: IdNameMixin;
    source_info_heat_transfer_coefficient_u?: IdNameMixin;
    source_info_thermal_resistance_coefficient_r?: IdNameMixin;
    latest_energy_yield_declaration?: number;
    latest_refrigerant_declaration?: number;
    planned_recycle: number;
    planned_resale: number;
    investments: MinimalInvestment[];
    global_investments?: MinimalInvestment[];
    step: ASSET_STEP;
    eligible_automatic_multi_year_plan?: boolean;
    end_year_note?: number;
    asset_notations: AssetNotation[];
    perimeter?: {id: number, name: string} | number;
    equipment_validated?: boolean;
    total_missing_asset?: number;
    total_missing_lifecycle?: number;
    total_missing_maintenance?: number;
    total_missing_compliance?: number;
    total_missing_documents?: number;
    compliance_alert_count?: number;
    maintenance_alert_count?: number;
    defaults?: number;
  }

  /**
   * `Asset` object with related objects attached.
   */
  export interface MappedAsset extends Asset, MappedImportableMixin {
    children?: MappedAsset[];
    zone_object: Zone;
    floor_object: AssetFloor;
    super_category: SuperCategory;
    category: Category;
    category_simple?: IdNameMixin;
    super_category_simple?: IdNameMixin;
    sub_category: SubCategory;
    asset_type_object: AssetType;
    ownership_type_object: OwnershipType;
    /** Ordered list of perimeters in which this asset is nested. */
    perimeters: Perimeter[];
    /** Name of the highest perimeter. */
    site_name: string;
    /** Joined names of the perimeters.  */
    perimeters_names_list: string;
    notation?: AssetComputedNotation;
    rawNotation?: AssetNotation;
    brand?: string;
    model?: string;
    power_source?: string;
    power?: string;
    last_notation?: AssetNotation;
  }

  export interface ChartPoint {
    x: number;
    y: number;
  }

  export interface InvestmentChartPoint extends ChartPoint {
    investment_label: string;
    price: number;
  }

  export interface AuditChartPoint extends ChartPoint {
    user: IdNameMixin;
    datetime: string;
    rating_reasons: KPI.RatingReason[];
  }
  export interface AssetLifecycle {
    installation_year: number;
    current_year_note: number;
    planned_eol: number;
    planned_eol_with_replacement: number;
    hypothesis_eol_with_replacement: number;
    historic_asset_curve: ChartPoint[];
    expected_asset_curve: ChartPoint[];
    next_asset_curve: ChartPoint[];
    is_replacement_planned: boolean;
    audit_points: AuditChartPoint[];
    planned_investments_points: InvestmentChartPoint[];
    hypothesis_investments_points: InvestmentChartPoint[];
    is_planned_replacement_of: boolean;
    is_hypothesis_replacement_of: boolean;
    consistency_check: {
      is_consistent: boolean;
      max_suggested_lifetime: number;
      min_suggested_lifetime: number;
      min_suggested_curve: ChartPoint[];
      max_suggested_curve: ChartPoint[];
      suggested_start_x: number;
      is_source_model: boolean;
    };
  }

  export interface MinimalInvestment {
    id: number;
    label: string;
    investment_type: number;
    status: number;
    final_schedule: number;
    current_price?: number;
    status_object?: InvestmentStatus;
    price_sheet?: number;
  }

  export interface MappedMinimalAsset {
    id: number;
    label: string;
    perimeter: Perimeter;
    site: Perimeter;
    super_category: number;
    category: number;
    asset_type: number;
    asset_type_object: AssetType;
    quantity_unit: QuantityUnit;
    is_quantity_consistent: boolean;
    sub_category_object?: SubCategory;
    pictures: Picture[];
    last_evaluation: MappedAssetNotation;
    planned_investments: MinimalInvestment[];
    hypothesis_investments: MinimalInvestment[];
    hypothesis_replacement?: MinimalInvestment;
    quantity: number;
    // Fonctional purpose
    technical_state_string: string;
    technical_state_color: string;
    replaced_by?: number;
    end_of_life?: number;
    installation_year?: number;
    theoretical_end_of_life?: number;
    eligible_automatic_multi_year_plan?: boolean;
    end_year_note?: number;
    defaults?: number;
  }

  /**
   * Picture of an `Asset`.
   */
  export interface AssetPicture extends Picture {
    /** Id. of the associate `Asset` */
    asset: number;
  }

  /**
   * `AuditQuestion` answer following an `Aset`.
   */
  export interface AssetNotation {
    id: number;
    /** Id. of the `Asset`. */
    asset: number;
    audit_year: number;
    /** Id. of the `AuditQuestionItem`. */
    question_item: number;
    note: number;
    datetime: string;
    user: number;
    user_object?: User;
  }

  /**
   * `AssetOpex` object — OPEX (maintenance cost) linked to an equipment.
   */
  export interface AssetOpex {
    id: number;
    asset: number;
    label: string;
    amount: number | string;
    year: number;
    type: 'preventive' | 'corrective' | 'breakdown';
    type_display?: string;
    created_at: string;
    updated_at: string;
  }

  /**
   * `RatingReason` object.
   */
  export interface RatingReason extends IdNameMixin {
    default_for_states: number[];
    only_for_states: number[];
    ordering: number;
    display_in_reports: boolean;
  }

  /**
   * `AssetNotation` object with related objects attached.
   */
  export interface MappedAssetNotation extends AssetNotation {
    audit_question_object: AuditQuestion;
    audit_question_item_object: AuditQuestionItem;
  }

  /**
   * Payload for updating audit notation.
   */
  export interface AuditNotationPayload {
    notes: Array<{
      question_item: number;
      note: number;
      mandatory: boolean;
      rating_reasons?: number[];
      comment?: string | null;
    }>;
  }

  /**
   * Execution of `Investment`.
   */
  export interface InvestmentExecution extends IdNameMixin, OrderingMixin {}

  /**
   * Set of investments that a user can compare to formulate different investment plan scenarii.
   */
  export interface InvestmentScenario extends IdNameMixin {
    is_main: boolean;
    created_by: number;
    created_on: string;
    is_flagged: boolean;
    color: string;
  }

  export interface InvestmentLink extends IdNameMixin {}

  export enum InstructionAsset {
    No=0,
    Yes=1,
    Optional=2
  }
  /**
   * Type of an `Investment`.
   */
  export interface InvestmentType extends IdNameMixin, IsDefaultMixin, OrderingMixin {
    only_for_asset_types: number[];
    replacement?: boolean;
    renew?: boolean;
    maintenance?: boolean;
    creation_of_equipment?: boolean;
    instruction_current_asset?: InstructionAsset;
    instruction_next_asset?: InstructionAsset;
  }

  /**
   * Reason for an `Investment`.
   */
  export interface InvestmentReason extends IdNameMixin, IsDefaultMixin {
    only_for_investment_types: number[];
    background_color: string;
    disabled: boolean;
    ordering: number;
  }

  export type InvestmentStatusAttribute =
    | "hypothesis"
    | "is_request"
    | "is_locked"
    | "is_validated"
    | "is_proposed"
    | "is_closed"
    | "is_cancelled"
    | "is_engaged";

  /**
   * Status of an `Investment`.
   */
  export interface InvestmentStatus extends IdNameMixin, IsDefaultMixin, OrderingMixin {
    hypothesis?: boolean;
    is_request: boolean;
    is_locked: boolean;
    is_validated: boolean;
    is_pre_validated: boolean;
    is_engaged: boolean;
    is_proposed: boolean;
    is_closed: boolean;
    is_cancelled: boolean;
    show_in_charts: boolean;
    columns: string[];
    color: string;
    related_initiative_can_be_unplanned: boolean;
    investment_updatable_fields: string[];
  }

  /**
   * Define an oriented transition between two `InvestmentStatus`
   */
  export interface InvestmentStatusTransition {
    id: number;
    /** Id. of the departure `InvestmentStatus` */
    from_status: number;
    /** Id. of the arrival `InvestmentStatus`*/
    to_status: number;
  }

  /**
   * The budget origin of the investment
   */
  export interface InvestmentBudgetOrigin extends IdNameMixin {
    is_default: boolean;
    price_list: boolean;
  }

  /**
   * Category that bears legacy client nomenclature
   */
  export interface InvestmentCategory extends IdNameMixin {
    children: InvestmentCategory[];
    full_name: string;
    /** Id. list of linked categories */
    categories: number[];
    /** Id. list of linked subCategories */
    subcategories: number[];
    parent_id: number;
    code: string;
  }

  /**
   * Picture of an `Investment`
   */
  export interface InvestmentPicture extends Picture {
    /** Id. of the associate `Investment` */
    investment: number;
  }

  /**
   * Reason of a `Postponement`.
   */
  export interface PostponementReason extends IdNameMixin {
    ordering: number;
    is_default: boolean;
  }

  /**
   * Postponement of an `Investment`.
   */
  export interface Postponement {
    /** Id. of the associate `Investment` */
    investment: number;
    year_from: number;
    year_to: number;
    price: string | number;
    /** Id. of the associate `InvestmentStatus` */
    status: number;
    /** Id. of the associate `PostponementReason` */
    reason: number;
  }

  /**
   * Postponement of an `Investment`.
   */
  export interface MappedPostponement extends Postponement {
    investment_object: MappedInvestment;
    reason_object: PostponementReason;
  }

  export type InvestmentPostponementRowsData = {
    id: string;
    label: string;
    current_price: number;
    initial_schedule_to: number;
    status: string;
    asset: {
      id: number;
      label: string;
    };
    site: string;
    super_category: string;
    category: string;
    sub_category: string;
  }[];

  /**
   * A company in charge of an `Investment`.
   */
  export interface Supplier extends IdNameMixin {}

  /**
   * Type of billing for an `Investment`.
   */
  export interface Billing extends IdNameMixin {}

  /**
   * A partner paying a part of an `Investment`.
   */
  export interface Sponsor extends IdNameMixin {
    perimeters: number[];
    display_in_spreadsheet_filters: boolean;
  }

  /**
   * Percentage paid by a partner for an `Investment`.
   */
  export interface InvestmentSponsorship {
    /** Id. of a `Sponsor`. */
    sponsor: {
      id: number;
    };
    percentage: number;
  }

  export interface InvestmentInvoiceRecipient extends Sponsor {}

  /**
   * Percentage paid by a partner for an `Investment`.
   */
  export interface InvestmentInvoicing {
    /** Id. of an `InvestmentInvoiceRecipient`. */
    recipient: {
      id: number;
    };
    percentage: number;
  }

  /**
   * Priority of an `Investment`.
   */
  export enum InvestmentPriorityEnum {
    PRIORITY_VERY_HIGH = 4, // Meh
    PRIORITY_HIGH = 1,
    PRIORITY_MEDIUM = 2,
    PRIORITY_LOW = 3
  }

  export interface InvestmentPriority extends IdNameMixin {
    is_default: boolean;
    is_flash_investment: boolean;
    color?: string;
    ordering: number;
  }

  export interface InvestmentPriorityObject extends IdNameMixin {
    is_flash_investment: boolean;
    color?: string;
  }

  /**
   * Document specification of an `Investment`.
   */
  export interface InvestmentDocument {
    id: number;
    /** Id. of the `DocumentType`. */
    document_type: number;
    /** Whether this document is required for this investment. */
    mandatory: boolean;
    /** Number of attached documents of this type on this investment. */
    count: number;
  }

  /**
   * An `Investment` may be spread across several years.
   */
  export interface InvestmentSlice {
    id: number;
    year: number;
    status: number;
    price: string | number;
    additional_price?: string | number;
    add_price_reason?: string;
    is_extra_work?: boolean;
  }

  /**
   * Regroups for a year and an investment the amount of the money spent and the remaining one
   */
  export interface InvestmentSpendingSlice {
    id: number;
    year: number;
    at_date: string;
    spent: string | number;
    remaining: string | number;
  }

  /**
   * Import status of an `Investment`.
   */
  export enum ImportableInvestmentStatus {
    ASSET_LINK,
    READY,
    VALIDATED
  }

  /**
   * Action on an asset or perimeter to improve the state.
   */
  export interface Investment extends ImportableMixin<ImportableInvestmentStatus> {
    id: number;
    created_on: string;
    label: string;
    /** Id. of the related asset. */
    asset?: number;
    /** Id. of the related building. */
    building?: number;
    /** Id. of the `InvestmentScenario`. */
    scenario: number;
    /** Id. of the `InvestmentStatus`. */
    status: number;
    /** Id. of the `InvestmentType`. */
    investment_type: number;
    priority?: InvestmentPriority;
    quantity: number;
    /** Budget reference coming from the related asset. */
    budget_reference: string | number;
    /** Parts of the investment. */
    slices: InvestmentSlice[];
    /**  For a year the amount of the money spent and the remaining one*/
    spending_slices: InvestmentSpendingSlice[];
    billing_type: number;
    supplier: number;
    sponsorships: InvestmentSponsorship[];
    /** Initial scheduled start year. */
    initial_schedule: number;
    /** Initial scheduled end year. */
    initial_schedule_to: number;
    /** Start year in which the investment actually happened. */
    final_schedule: number;
    /** End year in which the investment actually happened. */
    final_schedule_to: number;
    comments: string;
    local_id: string;
    taxonomy_objective: IdNameMixin;
    taxonomy_category: TaxonomyCategory;
    reasons: number[];
    duration_deviation: number;
    lifetime_after_investment: number;
    /** Number of related `Tasks`. */
    nb_tasks: number;
    documents: InvestmentDocument[];
    category: number;
    sub_category: number;
    /** True if the investment has no impact on the asset technical state  */
    has_no_impact: boolean;
    /** Temporary hard-coded additional data. */
    is_capex: boolean;
    energy_saving: string | number;
    investment_category: number;
    /** Id. of `InvestmentExecution` */
    execution: number;
    budget_origin: InvestmentBudgetOrigin;
    initiative: MinimalInitiative;
    further_information: FurtherInformation;
    price_sheet?: number;
    next_asset?: number;
    current_price?: number;
    technical_state_notation?: number;
    period?: number[];
    original_investment?: number;
    impact_roadmap_indicators: number[];
    budget_scopes: number[];
    status_object?: InvestmentStatus;
    projects: Partial<Project>[];
    reference?: string;
    reference_prefix?: string;
    custom_code?: string;
    project_code?: string;
    gtp_code?: string;
    quote_part?: {
      id: number;
      name: string;
    };
    linked_to?: number;
    super_category?: number;
  }

  export interface UpdateInvestment extends Investment {
    quote_part_id?: number;
  }

  export interface FurtherInformation extends IdNameMixin {}

  /**
   * `InvestmentSlice` object with related objects attached.
   */
  export interface MappedInvestmentSlice extends InvestmentSlice {
    status_object: InvestmentStatus;
  }

  /**
   * `Investment` object with related objects attached.
   */
  export interface MappedInvestment extends Investment, MappedImportableMixin {
    asset_object: MappedAsset;
    investment_type_object: InvestmentType;
    /** Map stating if the `InvestmentReason` is enabled for this investment. */
    reasons_map: { [reason_id: number]: boolean };
    execution_object: InvestmentExecution;
    /** Ordered list of perimeters in which this asset is nested. */
    perimeters: Perimeter[];
    multi_perimeter: PerimeterReferenceDataMap;
    /** Name of the highest perimeter. */
    site_name: string;
    /** Joined names of the perimeters.  */
    perimeters_names_list: string;
    /** The id. of the associate building (now mandatory) */
    building: number;
    status_object: InvestmentStatus;
    priority_object: InvestmentPriorityObject;
    investment_category_object: InvestmentCategory;
    super_category_object: SuperCategory;
    category_object: Category;
    sub_category_object: SubCategory;
    budget_origin: InvestmentBudgetOrigin;
    zone_name?: string; // useful for the display in the spreadsheet
    asset_label?: string; // useful for the display in the spreadsheet
    payback?: number;
    impact_roadmap_indicators: number[];
    current_price?: number;
    invoicings?: InvestmentInvoicing[];
    refacturation?: boolean;
  }

  /**
   * `AuditQuestion` answer following an `Investment`.
   */
  export interface InvestmentNotation {
    id: number;
    /** Id. of the `Investment`. */
    investment: number;
    /** Id. of the `AuditQuestionItem`. */
    question_item: number;
    note: number;
    datetime: string;
    user: number;
  }

  /**
   * `InvestmentNotation` object with related objects attached.
   */
  export interface MappedInvestmentNotation extends InvestmentNotation {
    audit_question_object: AuditQuestion;
    audit_question_item_object: AuditQuestionItem;
  }

  /**
   * Notation scale.
   */
  export enum NotationLevel {
    CRITICAL,
    WARNING,
    ACCEPTABLE,
    GOOD
  }

  export interface AssetWithNotations extends MappedAsset {
    notation: KPI.AssetComputedNotation;
    technical_state_notation: {
      level: number;
      notation: number;
    };
    cluster: any;
    ref_data: { [key: number]: KPI.ReferenceField };
    investments: KPI.MappedInvestment[];
    last_notation: KPI.MappedAssetNotation;
  }

  /**
   * Structure for a set of notation.
   */
  export interface BaseComputedNotation {
    /** Start year. */
    start_year: number;
    /** End year. */
    end_year: number;
    /** Total invesment amout */
    capex?: number;
    /** Number of investments relative to capex */
    nb_capex?: number;
    total_quantity?: number;
    nb_assets: {
      [year: number]: {
        /** The number of assets notations at this year. */
        notations: number;
        /** Number of assets notes computed. */
        computed: number;
        /** Number of assets without notations. */
        no_notations: number;
        /** Number of assets whose asset type has a notation weight > 0. */
        with_ponderation?: number;
      };
    };
    nb_notations: {
      [year: number]: {
        /** The number of notations at this year. */
        notations: number;
        /** Number of notes computed. */
        computed: number;
      };
    };
    /** The notation values. */
    notation: {
      [year: number]: {
        /** The notation at this year. */
        notation: number;
        /** The corresponding level. */
        level: number;
        is_next_asset_note: boolean;
      };
    };
  }

  export interface SimpleCapex {
    capex: number;
    investment_number: number;
    with_impact: number;
    initiative_related_capex?: number;
    initiative_related_investment_nb?: number;
  }

  export interface BaseComputedCapex {
    /** Total invesment amout */
    capex?: number;
    nb_capex?: number;
    /** Start year. */
    start_year: number;
    /** End year. */
    end_year: number;
    /** Total hypothesis invesment amout */
    hypothesis_capex?: number;
    nb_hypothesis_capex?: number;
    /* replacement year if any */
    replacement_year?: number;
    hypothesis_replacement_year?: number;
  }

  /**
   * Global notation.
   */
  export interface GlobalComputedNotation extends BaseComputedNotation {
    /** child supercategory notations */
    super_categories: SuperCategoryComputedNotation[];
  }

  /**
   * Notation of a `SuperCategory`.
   */
  export interface SuperCategoryComputedNotation extends BaseComputedNotation {
    /** Id. of the `SuperCategory`. */
    super_category: number;
    /** child category notations */
    categories: CategoryComputedNotation[];
  }

  /**
   * Notation of a `Category`.
   */
  export interface CategoryComputedNotation extends BaseComputedNotation {
    /** Id. of the `Category`. */
    category: number;
    /** child sub-category notations */
    sub_categories: SubCategoryComputedNotation[];
  }

  /**
   * Notation of a `SubCategory`.
   */
  export interface SubCategoryComputedNotation extends BaseComputedNotation {
    /** Id. of the `SubCategory`. */
    sub_category: number;
    /** Whether or not the notation is computed from at least one partial audit. */
    partial_audit: boolean;
    /** child asset type notations */
    asset_types: AssetTypeComputedNotation[];
  }

  /**
   * Notation of an `AssetType`.
   */
  export interface AssetTypeComputedNotation extends BaseComputedNotation {
    /** Id. of the `AssetType`. */
    asset_type: number;
    /** provided with depth parameter to 1 */
    assets?: AssetComputedNotation[];
  }

  /**
   * Notation of an `AuditQuestion`.
   */
  export interface QuestionComputedNotation extends BaseComputedNotation {
    /** Id. of the `AuditQuestion`. */
    question: number;
  }

  /**
   * Notation of an `Asset`.
   */
  export interface AssetComputedNotation extends BaseComputedNotation {
    /** Id. of the `Asset`. */
    asset: number;
    /** Id. of the asset `OwnershipType`. */
    ownership_type: number;
    /** Set of `AuditQuestion` notations of the asset. */
    questions: QuestionComputedNotation[];
    /** Asset's end of life year */
    end_of_life?: number;
  }

  /**
   * Notation of a `Perimeter`.
   */
  export interface PerimeterComputedNotation extends BaseComputedNotation {
    /** Id. of the `Perimeter`. */
    perimeter: number;
  }

  /**
   * A KPI computed from items of the roadmap.
   */
  export interface RoadmapIndicator extends IdNameMixin {
    mnemonic: string;
    order: number;
    shown_in_report: boolean;
    /** Set of `Chapter` ids associated to this indicator. */
    chapters: number[];
    budget: number;
    notation?: KPI.RoadmapIndicatorNotation;
    notationDiff?: number;
    is_energy_carbon?: boolean;
    technical_state_related?: boolean;
    fullname: string;
  }

  /**
   * A group of roadmap questions.
   */
  export interface Chapter {
    id: number;
    subject: string;
    index: number;
    full_index: string;
    themes: number[];
    label: string;
    only_for_types: number[];
    only_for_clusters: number[];
  }

  /**
   * A choice for an `Item`.
   */
  export interface ItemChoice {
    id: number;
    text: string;
  }

  /**
   * An single question in a `Chapter`.
   */
  export interface Item {
    id: number;
    text: string;
    information: string;
    choices: ItemChoice[];
    perimeterId: number;
    matchingPerimeters: Perimeter[];
    instruction_sub_category?: IdNameMixin;
  }

  /**
   * Structure of a set of roadmap notation.
   */
  export interface BaseRoadmapNotation {
    audit_year: number;
    notation: {
      current: number;
      done_points: number;
      total_points: number;
      confidence_index: number;
    };
  }

  /**
   * Notation of a single roadmap item.
   */
  export interface ItemNotation extends BaseRoadmapNotation, LastChangeMixin {
    /** Id. of the `Item`. */
    item: number;
    /** List of selected `ItemChoice` ids. */
    selected_choices: number[];
  }

  /**
   * Notation of a control point over a perimeter.
   */
  export interface ControlPointNotation extends BaseRoadmapNotation, LastChangeMixin {
    /** Id. of the `Perimeter`. */
    perimeter: number;
    /** Notations for each item involved in the notation of this control point */
    items_notations: ItemNotation[];
  }

  /**
   * Notation of a roadmap chapter.
   */
  export interface ChapterNotation extends BaseRoadmapNotation {
    /** Id. of the `Chapter`. */
    chapter: number;
  }

  /**
   * Notation of a roadmap indicator.
   */
  export interface RoadmapIndicatorNotation extends BaseRoadmapNotation {
    /** Id. of the `RoadmapIndicator`. */
    roadmap_indicator: number;
  }

  export interface RoadmapItemAnswer {
    id: number;
    /** The perimeter id */
    perimeter: number;
    /** The RoadmapItem id */
    item: number;
    /** The audit id */
    audit: number;
    last_change_on: Date;
    /** The User id */
    last_change_by: number;
  }

  /**
   * Counters of control points for a `Perimeter`.
   */
  export interface ControlPointsStats {
    perimeter: number;
    acknowledged: number;
    score: number;
    remaining: number;
    total: number;
  }

  /**
   * Counters of control points for a year.
   */
  export interface ControlPointsStatsByYear {
    year: number;
    control_points_stats: ControlPointsStats[];
  }

  /**
   * Definition of a roadmap confidence index for an `AuditQuestion`.
   */
  export interface AuditQuestionConfidenceIndex {
    id: number;
    /** Id. of the `AuditQuestion`. */
    audit_question: number;
    /**
     * Id. of the related `OwnershipType`.
     * Used to compute a different score for technical state
     * depending on the ownership type of the asset.
     */
    ownership_type?: number;
    acknowledged?: number;
    score?: number;
    remaining?: number;
    total?: number;
  }

  export enum MissingNotationReason {
    NOT_SET = 0,
    STOPPED_ABNORMAL = 1,
    STOPPED_INSTRUCTION = 2,
    BROKEN = 3,
    ACCESS = 4,
    WAITING_EVALUATION = 5,
    IMPOSSIBLE = 6,
    MANAGER_UNAVAILABLE = 7,
    MANAGER_DOESNT_KNOW = 8,
    MAX_VALUE // Not a real value but useful for looping on each value
  }

  export enum Confidence {
    LOW = 1,
    MEDIUM = 2,
    HIGH = 3,
    MAX_VALUE // Not a real value but useful for looping on each value
  }

  /**
   * Definition of a object containing a change for a field
   */
  export interface ChangeItem {
    /** Use to store the id if it is a children object */
    object_id?: number;
    /** Use to store the id if it is a children object */
    object_type?: string;
    field_name: string;
    old: string | boolean | number;
    new: string | boolean | number;
  }

  /**
   * Definition of an object regrouping the changes made by a user at a defined date
   */
  export interface HistoryItem {
    /** Id. of the `User` who had modified the object. */
    user: number;
    date: Date;
    changes: ChangeItem[];
    isCreation?: boolean;
  }

  /**
   * Definition of the history of an object
   */
  export interface History {
    history_items: HistoryItem[];
  }

  export interface YearAndStatusHistoryItem {
    year: number;
    status: string;
    date: Date;
  }

  /**
   * Regroups slices by year and by status
   */
  export interface InvestmentSlicesByYear {
    year: number;
    investment: MappedInvestment;
    budget: number;
    slicesByStatus: { [statusId: number]: InvestmentSlice };
    spendingSlice: InvestmentSpendingSlice;
    isExtraWork: boolean;
  }

  /**
   * Regroups slices by year and by status with information about postponement
   */
  export interface PostponementSlicesByYear extends InvestmentSlicesByYear {
    postponement: KPI.MappedPostponement;
  }

  /**
   * Definition of a document
   */
  export interface Document {
    id: number;
    title: string;
    type: string;
    created_on: Date;
    created_by: string;
    reservations: string;
    link: string;
    equipment: string;
    comments: string;
    expiry_date?: string;
    company?: string;
    duration?: string;
    linked_equipment?: number[];
    is_maintenance_contract?: boolean;
    linked_to?: string;
    asset_label?: string;
    investment_label?: string;
    initiative_label?: string;
    perimeter_label?: string;
  }

  /**
   * Definition of an excel column for the import
   */
  export interface ColumnDefinition {
    name: string;
    field: string;
    map_with: string;
    multiple: boolean;
  }

  /**
   * Definition of mapped data
   */
  export interface MappedData {
    [type: string]: { [clientValue: string]: number };
  }

  /**
   * Definition of the mapping for the import
   */
  export interface Mapping {
    id: number;
    mapped_data: MappedData;
  }

  /**
   * A cost denominator - eg. the surface area of a store
   */
  export interface CostDenominator extends IdNameMixin {
    description: string;
    unit: string;
  }

  /**
   * A cost centre (parent) or cost item (child)
   * todo - i think we should have a cost centre and cost item separately
   */
  export interface CostElement extends IdNameMixin {
    type: string;
    code: string;
    level: number;
    slug: string;
    amount: number;
    denominator: CostDenominator;
    default_denominator: CostDenominator;
    children: CostElement[];
  }

  /**
   * A lightweight representation of a spend for a cost item
   */
  export interface Spend {
    id: number;
    cost_item: number;
    value: number;
  }

  /**
   * Benchmark information for OPEX
   */
  export interface CostBenchmark {
    sizeValue: number;
    minimumValue: number;
    maximumValue: number;
    averageValue: number;
    medianValue: number;
    totalValue: number;
    medianDenominator: number;
  }

  /**
   * Represents a row on the opex report table
   */
  export interface OpexOverviewReportElement {
    label: string;
    value: number;
    level: number;
    name: string;
    scaledValue: number;
    medianSavings: number;
    elementId: number;
    savingOpportunities: number;
  }

  /**
   * The contents of the default OPEX report
   */
  export interface OpexOverviewReport {
    values: OpexOverviewReportElement[];
    initiativeThreshold: number;
    initiativesOverThreshold: number;
    percentOfOverspend: number;
    largestOverspendName: string;
    totalOverspend: number;
  }

  /**
   * Contains the total for investment
   */
  export interface InvestmenNbAndTotal {
    total: number;
    total_linked_to_initiative_energy: number;
    nb_inv: number;
    nb_inv_linked_to_initiative_energy: number;
  }

  /**
   * Contains the total for investment
   */
  export interface InvestmentTotal {
    total: number;
    total_linked_to_initiative_energy: number;
    planned_total: number;
    planned_total_linked_to_initiative_energy: number;
  }

  /**
   * Contains the total budget group by scenario/status/year
   */
  export interface InvestmentTotalByStatus {
    [scenarioId: number]: {
      [statusId: number]: {
        [year: number]: {
          budget: number;
          invest_nb: number;
          invest_linked_to_initiative_energy_budget: number;
          invest_linked_to_initiative_energy_nb: number;
        };
      };
    };
  }

  export interface GlobalInvestments {
    [scenarioId: number]: {
      [category: string]: {
        [year: number]: {
          budget: number;
          invest_nb: number;
          invest_linked_to_initiative_energy_budget: number;
          invest_linked_to_initiative_energy_nb: number;
        };
      };
    };
  }

  /**
   * Contains the investment by status
   */
  export interface InvestmentByStatus {
    [statusId: number]: {
      budget: number;
      invest_nb: number;
    };
  }

  export interface InvestmentByCategory {
    [statusId: number]: {
      budget: number;
      invest_nb: number;
    };
  }

  export interface InvestmentByYearAndCategory {
    [year: number]: {
      [statusId: number]: {
        budget: number;
        invest_nb: number;
      };
    };
    total_invest: number;
  }

  /**
   * Contains the total of in project budget, of out plan budget and of postponed budget
   */
  export interface InvestmentTotalBudgetPlan {
    in_budget_plan: {
      [statusId: number]: InvestmenNbAndTotal;
    };
    out_budget_plan: {
      [statusId: number]: InvestmenNbAndTotal;
    };
    postponed: InvestmenNbAndTotal;
    nb_closed_budget_plan: number;
    nb_active_budget_plan: number;
  }

  export interface SavingsSummary {
    savings: number;
    totalInitiatives: number;
    co2Savings: number;
    actionsBudget: number;
  }

  export interface ProjectsSummary {
    rowCount: number;
    projectBudget: number;
    projectInitiativeInvestNb: number;
    projectInitiativeInvestBudget: number;
  }

  export type UpdatableInitiativeInstance = RequireAtLeastOne<
    {
      id: number;
      savings_start_at?: string; // YYYY-MM-DD
      status?: number;
    },
    "savings_start_at" | "status"
  >;

  export interface InitiativeInstance {
    id: number;
    initiative: number;
    scenario: number;
    status: number;
    scenario_object?: InvestmentScenario;
    status_object?: InitiativeStatus;
  }

  export interface GainPrecision extends IdNameMixin {}

  export interface GainOrigin extends IdNameMixin {}

  export interface InitiativeGain {
    energy_type_id: number;
    id: number;
    initiative_id: number;
    real_greenhouse_gas_savings: number;
    real_savings: number;
    refrigerant_type_id: number;
    estimated_greenhouse_gas_savings_first_year: number;
    estimated_greenhouse_gas_savings_full_year: number;
    estimated_savings_first_year: number;
    estimated_savings_full_year: number;
    project_greenhouse_gas_savings_first_year: number;
    project_greenhouse_gas_savings_full_year: number;
    project_savings_first_year: number;
    project_savings_full_year: number;
    theoretical_greenhouse_gas_savings_first_year?: number;
    theoretical_greenhouse_gas_savings_full_year: number;
    theoretical_savings_first_year?: number;
    theoretical_savings_full_year: number;
    gain_precision: GainPrecision;
    gain_origin: GainOrigin;
    full_stop: boolean;
  }

  export interface MappedInitiative {
    id: number;
    label: string;
    initiative_type: InitiativeType;
    /** The initiative_type's own parent InitiativeType (its "family"/section, e.g.
     * Déchets vs Énergie) - resolved client-side by InitiativesService.mapInitiatives,
     * not part of the raw API payload. */
    initiative_type_parent?: InitiativeType;
    status: InitiativeStatus;
    created_on: string;
    perimeter: Perimeter;
    site: Perimeter;
    reason: string;
    description: string;
    comments: string;
    savings_start_at: string;
    confidence_on_theoretical_savings_name: string;
    confidence_on_theoretical_savings: number;
    confidence_on_feasibility: number;
    confidence_on_feasibility_name: string;
    yearly_cost_savings: number;
    energy_losses: number;
    created_by: User;
    owner: InitiativeOwner;
    assets: Asset[];
    investment: Investment;
    budget: number;
    budget_string?: string;
    invest_period: string;
    payback_time: number;
    cee_bonus: number;
    emission_category?: {
      name: string;
      scope: number;
      is_calculated: boolean;
    };
    instances: InitiativeInstance[];
    responsible: string;
    initially_proposed_by: string;
    code: string;
    gains: InitiativeGain[];
    quantity: number;
    projects: Partial<Project[]>;
    recurrency: boolean;
    tags: InitiativeTag[];
  }

  export interface InitiativeTag extends IdNameMixin {}

  export interface Initiative {
    id: number;
    label: string;
    initiative_type: number;
    status: number;
    created_on: string;
    perimeter: number;
    site: number;
    reason: string;
    description: string;
    comments: string;
    savings_start_at: string;
    confidence_on_theoretical_savings: number;
    confidence_on_feasibility: number;
    yearly_cost_savings: number;
    energy_losses: number;
    created_by: number;
    owner: number;
    assets: Asset[];
    investment: Investment;
    budget: number;
    invest_period: string;
    payback_time: number;
    cee_bonus: number;
    emission_category?: {
      name: string;
      scope: number;
      is_calculated: boolean;
    };
    instances: InitiativeInstance[];
    responsible: string;
    initially_proposed_by: string;
    code: string;
    gains: InitiativeGain[];
    quantity: number;
    projects: Partial<Project[]>;
    recurrency: boolean;
    tags: InitiativeTag[];
  }

  export interface MinimalInitiativeWithSavings {
    id: number;
    label: string;
    initiative_type: number;
    status: number;
    energy_type_id: number;
    theoretical_savings_first_year: number;
    theoretical_savings_full_year: number;
    investments: number[];
  }

  export interface MinimalInitiative {
    id: number;
    label: string;
    initiative_type: number;
    status: number;
    gains: InitiativeGain[];
  }

  export interface MappedMinimalInitiative {
    id: number;
    label: string;
    initiative_type: InitiativeType;
    status: InitiativeStatus;
    energy_type: InitiativeEnergyType;
  }

  export type InitiativeStatusAttribute = "hypothesis" | "cancelled" | "completed" | "is_planned" | "in_progress";

  export type InitiativeStatus = InvestmentStatus & {setup_reference: string};

  export type GainType = "None" | "ENERGY" | "CARBON" | "QUIT_FOSSIL";
  export interface InitiativeType extends IdNameMixin {
    setup_reference: string;
    parent: number;
    gain_type: GainType;
    aligned_taxonomy: boolean;
    fidji_available: boolean;
    updated_at: string;
    description: string;
    default_confidence_on_feasibility: number;
    default_quantity: number;
    default_recurrency: boolean;
    friendly_name: string;
    quantity_unit: QuantityUnit;
    emission_category: number;
    code?: string;
    order?: number;
  }

  export interface InitiativeOwner extends IdNameMixin {}

  export interface InitiativeTag extends IdNameMixin {
    order: number;
  }

  export type GammePeriodicity = '6_MONTHS' | '1_YEAR' | '3_YEARS' | '5_YEARS' | '10_YEARS' | 'PERMANENT';

  export interface GenericGamme {
    id: number;
    tag: string;
    label: string;
    country: { id: number; name: string; iso_3116: string }[];
    periodicity: GammePeriodicity | '';
    regulatory_text_url: string;
    regulatory_comment: string;
    comment_law: string;
    theme: { id: number; name: string } | null;
    sub_theme: { id: number; name: string } | null;
    clusters: { id: number; name: string; setup_reference: string }[];
    is_active: boolean;
    site_staff: boolean;
    maintenance_company: boolean;
    approved_body: boolean;
    safety_commission: boolean;
    linked_document_type: { id: number; label: string } | null;
    keywords: string[];
    ia_gtp: boolean;
    sub_lots: { id: number; name: string; setup_reference: string }[];
    asset_types: { id: number; name: string; setup_reference: string }[];
    suggested_companies: string[];
    created_at: string;
    updated_at: string;
  }

  export type GammeSiteStatus = 'waiting' | 'to_process' | 'problem' | 'ok' | 'late' | '';

  export interface GammeSite {
    id: number;
    gamme: number;
    perimeter: { id: number; name: string; parent_id: number | null; parent_name: string | null };
    perimeter_id?: number;
    is_active: boolean;
    comment: string;
    service_provider: string;
    responsible_conformity: string;
    status: GammeSiteStatus;
    last_intervention_date: string | null;
    next_visit_date: string | null;
    label: string;
    tag: string;
    periodicity: GammePeriodicity | '';
    regulatory_text_url: string;
    regulatory_comment: string;
    comment_law: string;
    theme: { id: number; name: string } | null;
    sub_theme: { id: number; name: string } | null;
    gamme_is_active: boolean;
    site_staff: boolean;
    maintenance_company: boolean;
    approved_body: boolean;
    safety_commission: boolean;
    linked_document_type: { id: number; label: string } | null;
    suggested_companies: string[];
    sub_lots: { id: number; name: string; setup_reference: string }[];
    asset_types: { id: number; name: string; setup_reference: string }[];
    created_at: string;
    updated_at: string;
  }

  export interface GenericGammeActivation {
    id: number;
    gamme: number;
    cluster: number;
    is_active: boolean;
    created_at: string;
    updated_at: string;
  }

  export interface InitiativeEnergyType extends IdNameMixin {
    unit: IdNameMixin;
    color: string;
    order: number;
    is_electricity: boolean;
    is_unspecified: boolean;
    is_default: boolean;
  }
  export interface EnergyPrice {
    year: number;
    base_price: number;
  }
  export interface HypothesisEnergyType extends IdNameMixin {
    inflation_rate: number;
    prices: EnergyPrice[];
  }

  export interface Country extends IdNameMixin {}
  export interface InflationHypothesis extends IdNameMixin {
    energy_types: HypothesisEnergyType[];
    country: Country;
  }

  export interface InflationZone extends IdNameMixin {}

  export interface ProjectType extends IdNameMixin {
    theoretical_ratio: number;
  }

  export interface ProjectInvestment {
    id: number;
    label: string;
    status: number;
    status_object: InvestmentStatus;
    current_price: number;
  }

  export interface ProjectAsset {
    id: number;
    label: string;
  }

  export interface WriteableProjectInstance {
    id: number;
    status?: number;
    scenario?: number;
    savings_start_at?: string;
  }

  export interface ProjectInstance {
    id: number;
    status: InitiativeStatus;
    scenario: InvestmentScenario;
    savings_start_at: string;
  }

  export interface ProjectGain {
    energy_type: number;
    id: number;
    theoretical_greenhouse_gas_savings_first_year: number;
    theoretical_greenhouse_gas_savings_full_year: number;
    theoretical_savings_first_year: number;
    theoretical_savings_full_year: number;
  }

  export interface Project {
    id: number;
    label: string;
    project_type: ProjectType;
    perimeter: number;
    perimeter_object?: Perimeter;
    site?: Perimeter;
    start_date: string;
    end_date: string;
    probability: number;
    probability_label?: string;
    description: string;
    comments: string;
    total_price: number;
    assets_count: number;
    theoretical_ratio: number;
    real_ratio: number;
    initiatives_count: number;
    total_yearly_energy_savings: number;
    total_yearly_green_house_gas_savings: number;
    project_initiatives: MinimalInitiative[];
    investments: number[];
    assets: ProjectAsset[];
    next_assets: ProjectAsset[];
    instances: ProjectInstance[];
    global_gain: boolean;
    gains: ProjectGain[];
  }

  export interface Month {
    nb: number;
    name: string;
  }

  /**
   * CAPEX Insight Global Filters
   */
  export enum InsightGlobalFilter {
    ALL_INVESTMENTS,
    ONLY_PLANNED_INVESTMENTS
  }

  export interface PerimeterStats {
    notation: number;
    notation_string?: string;
    period_planned_budget: number;
    period_planned_budget_string?: string | string[];
    period_invest_nb: number;
    assets_count: number;
    energy_trajectory: {
      trend: number;
      consumption_ratio: number;
    };
    creation_year: number;
    last_audit_date: string;
    perimeter?: KPI.Perimeter;
    next_year_validated_capex: number;
    next_year_proposed_capex: number;
    next_year_request_capex: number;
  }

  export interface PerimeterIdsWithTrajectoryByEnergyType {
    [energyTypeId: number]: number[];
  }
  export interface PerimeterStatsDetails extends PerimeterStats {
    critical_assets_ids: number[];
    next_year: number;
    critical_assets_invest_next_year_nb: number;
    critical_assets_invest_next_year_budget: number;
    critical_assets_invest_next_year_budget_string?: string | string[];
    end_year: number;
    dead_assets_before_end_year_ids: number[];
    dead_assets_with_hypothesis_invest_nb: number;
    dead_assets_hypothesis_invest_budget: number;
    dead_assets_hypothesis_invest_budget_string?: string | string[];
    dead_assets_with_planned_invest_nb: number;
    dead_assets_planned_invest_budget: number;
    dead_assets_planned_invest_budget_string?: string | string[];
    assets_average_age: number;
    assets_with_higher_lifetime_percentage: number;
    perimeter_id_with_trajectory_by_energy_type: PerimeterIdsWithTrajectoryByEnergyType;
    impact_indicators: PerimeterStatsIndicator[];
    dead_assets_hypothesis_invest_ids: string[];
    dead_assets_planned_invest_ids: string[];
    critical_assets_invest_next_year_ids: string[];
  }

  export interface PerimeterStatsIndicator extends IdNameMixin {
    invest_nb: number;
    total_budget: number;
    budget_string?: string[];
    invest_ids: string[];
  }

  export interface ScenarioTrajectory {
    investments: {
      list: number[];
      amount: number;
      year_since?: number;
    };
    consumption_absolute: number;
    consumption_absolute_objective: number;
    consumption_absolute_reference: number;
    emission_absolute: number;
    emission_absolute_objective: number;
    emission_absolute_reference: number;
    scenario: IdNameMixin;
  }

  export interface PerimeterEnergyStats {
    site_area: number;
    consumption_last_12_months: {
      date_range: [string, string];
      consumption_absolute: number;
      emission_absolute: number;
    };
    trajectory_main_scenario: ScenarioTrajectory;
    trajectory_flagged_scenario: ScenarioTrajectory;
    ghg_emission_ratio_last_year_vs_two_years?: number;
    avoided_emissions_since_N_minus_1?: number;
    actions_stats: {
      completed_actions_number: number;
      completed_ratio: number;
      first_action_year: number;
      total_economies: number;
      past_actions_ids: number[];
      actions_ids: number[];
    };
  }

  /**
   * Trajectory Budget or Ratio filter for energy
   */
  export enum BudgetOrRatioFilter {
    TRAJECTORY_ONLY,
    CAPEX_AMOUNT
  }

  /**
   *
   */
  export enum EnergyOrCarbonTrajectoryFilter {
    ENERGY,
    CARBON
  }

  /**
   * Trajectory Budget or Ratio filter for carbon
   */
  export enum BudgetOrRatioCarbonFilter {
    TRAJECTORY_ONLY,
    CAPEX_AMOUNT
  }

  /**
   * Objective type selector for stranded assets
   */
  export enum ObjectiveTypeSelector {
    COMPANY_OBJECTIVE_KWHEF,
    SBTI_OBJECTIVE_KWHEF,
    COMPANY_OBJECTIVE_KGC02,
    SBTI_OBJECTIVE_KGC02
  }

  /**
   * Evaluation selector for stranded assets
   */
  export enum EvaluationSelector {
    BUILDINGS_COUNT,
    SURFACE,
    ASSET_VALUE
  }

  /**
   * Trajectory selector for stranded assets
   */
  export enum TrajectorySelector {
    ENERGY,
    CARBON
  }

  export enum PlannedInitiativeFilter {
    PLANNED_ONLY,
    ALL
  }

  export enum InitiativePrioritiesFilter {
    OVERVIEW,
    QUICK_WIN,
    MINOR_RENOVATION,
    MAJOR_RENOVATION,
    INITIATIVE_TYPE
  }

  export enum InitiativeSortingPriorityFilter {
    MOST_TO_LESS_PROFITABLE,
    MOST_TO_LESS_IMPACTFUL
  }

  export enum FeasibilityConsumptionOrEmissionFilter {
    CONSUMPTION,
    EMISSION
  }

  export enum ConsumptionType {
    RAW_CONSUMPTION = "consumption",
    CONSUMPTION_CORRECTED_CLIMATE = "consumption_corrected_climate",
    CONSUMPTION_CORRECTED_OFFICIAL = "consumption_corrected_official"
  }

  export type TConsumptionType = "consumption" | "consumption_corrected_climate" | "consumption_corrected_official";

  /**
   * Carbon Trajectory Electric Forecast Emission Factor filter
   */
  export enum ElectricityForecastEmissionFactorFilter {
    FIXED_FACTOR,
    CRREM_V18,
    CUSTOMIZED_BY_SITE,
    MIX_M0_RTE,
    MIX_M1_RTE,
    MIX_M23_RTE,
    MIX_N1_RTE,
    MIX_N2_RTE,
    MIX_N03_RTE
  }

  /**
   * Carbon Trajectory Urban Heating Forecast Emission Factor filter
   */
  export enum UrbanHeatingForecastEmissionFactorFilter {
    FIXED_FACTOR,
    CRREM_V18,
    CUSTOMIZED_BY_SITE,
    MIX_PRODUCTION_1,
    MIX_PRODUCTION_2,
    MIX_PRODUCTION_3
  }

  /**
   * Carbon Trajectory Urban Cold Forecast Emission Factor filter
   */
  export enum UrbanColdForecastEmissionFactorFilter {
    FIXED_FACTOR,
    CRREM_V18,
    CUSTOMIZED_BY_SITE,
    MIX_PRODUCTION_1,
    MIX_PRODUCTION_2,
    MIX_PRODUCTION_3
  }

  /**
   * CAPEX Insight Assets Filters
   */
  export enum InsightAssetsFilter {
    ALL_ASSETS,
    ONLY_UNIT_OR_COLLECTION,
    ONLY_WITH_MAINTENANCE_CODE
  }

  /**
   * Energy measurements filters
   */
  export enum EnergyMeasurementsFilter {
    CONSO,
    CONSOAREA,
    EMISSION,
    EMISSIONAREA
  }

  export enum ConsumptionAggregationsFilter {
    CONSO,
    CONSOAREA
  }

  export enum EmissionAggregationsFilter {
    EMISSION,
    EMISSIONAREA
  }

  export enum EnergyConsumptionFilter {
    RAW,
    CORRECTED_SEASON,
    DAY,
    MONTH,
    YEAR,
    TWELVESLIDING,
    PERCENT,
    KWH
  }

  export enum DecisionOverviewFilter {
    ALL_INVESTMENTS,
    ONLY_INITIATIVE_RELATED
  }

  export enum InvestmentsStatusFilter {
    ALL_INVESTMENTS,
    ONLY_PLANNED,
    ONLY_HYPOTHESIS
  }

  /**
   *
   */
  export interface CellEditorResult {
    id: number;
    field: string;
    value: any;
  }

  export interface EnergyReportSection {
    id: number;
    name: string;
    label: string;
    is_checked_by_default: boolean;
  }

  export interface CustomerConfig {
    name: string;
    default_latitude: number;
    default_longitude: number;
    default_zoom: number;
    picture: Picture;
    is_selected: boolean;
    reports_color: string;
    reports_fonts_color: string;
    show_technical_state_figures: boolean;
    investment_default_columns: string;
    can_access_workflow_flash_invest: boolean;
    can_access_workflow_annual_plan: boolean;
    minimal_assets_number: number;
    default_consumption: ConsumptionType;
    auto_investment_plan_sites_limit: number;
    can_access_audit_app: boolean;
    investment_code_field_name: string;
    is_fidji_projects: boolean;
    risk_module_enabled: boolean;
    show_investment_custom_code: boolean;
    show_investment_gtp_code: boolean;
    show_investment_project_code: boolean;
    is_show_edit: boolean;
    energy_carbon_settings_icon_display: boolean;
    open_action_sheet_full_screen: boolean;
    hide_action_tab: boolean;
    bouquet_of_works_tab_below_enery_carbon_trajectory: boolean;
    uniformat_code_display_in_equipment_sheet: boolean;
    inies_data_sheet_display_in_equipment_sheet: boolean;
    equipment_and_reserve_xls_export: boolean;
    defect_management: boolean;
    taxonomy_XLS_reporting: boolean;
    ESRS_XLS_reporting: boolean;
  }

  export interface SitesMapLegendItem {
    area_from: number;
    area_to: number;
    area_from_string?: string;
    area_to_string?: string;
    iconPath?: string;
    iconSelectedPath?: string;
  }

  export interface PriceSheet {
    id: number;
    label: string;
    average_price: number;
    min_price: number;
    max_price: number;
    quantity: number;
    quantity_unit: IdNameMixin;
    author: Omit<User, "permieter_permissions">;
    modified_on: Date;
    is_default: boolean;
    is_benchmark: boolean;
    is_customer: boolean;
    last_modification_author: User;
    comments: string;
    model: string;
    country_id: number;
    country: string;
    region_id: number;
    region: string;
    year: number;
    budget_scope: BudgetScope;
    is_in_use: boolean;
    asset_type: number;
    investment_type: InvestmentType | null;
  }

  export interface BudgetScope extends IdNameMixin {
    order: number;
    is_default: boolean;
    is_fourni_pose: boolean;
  }

  export interface PriceSheetCostItem {
    id: number;
    name: string;
  }

  export interface PriceSheetCostItemValue {
    cost_item: number;
    unit_price: number;
  }

  /**
   * No / Yes / I Don't know
   */
  export enum YesNoIDK {
    NO,
    YES,
    IDK
  }

  export interface AssetConsumption {
    asset_id: number;
    energy_consumption: number;
    perimeter_energy_consumption_id: number;
  }
  export interface PerimeterTrajectoryConsumption {
    id: number;
    perimeter_consumption_id: number;
    asset_consumptions: AssetConsumption[];
    year: number;
    consumption: number;
    consumption_corrected_climate: number;
    consumption_corrected_official: number;
    area: number;
    updated_on: string;
    trajectory: 1;
    perimeter?: PerimeterConsumption;
    energy_type?: InitiativeEnergyType;
    comment: string;
  }
  export interface PerimeterTrajectory {
    id: number;
    perimeter_consumptions: PerimeterTrajectoryConsumption[];
    energy_type: InitiativeEnergyType;
    created_on: string;
    perimeter: number;
    area: number;
    declaration_month: number;
    declaration_updated_on: string;
    is_mandatory: boolean;
  }

  /** Real PerimeterEnergyTrajectory model from Backend */
  export interface PerimeterEnergyTrajectory {
    id: number;
    perimeter: number;
    created_on: Date;
    energy_type: number;
    refrigerant_type: number;
    area: number;
    declaration_month: number;
    declaration_updated_on: string;
    is_mandatory: boolean;
    trajectory_records: TrajectoryRecord[];
  }

  export interface TrajectoryRecord {
    id: number;
    trajectory: number;
    year: number;
    consumption: number;
    emission: number;
    updated_on: string;
  }

  export interface RefrigerantType extends IdNameMixin {
    gwp_20_years: number;
  }

  export interface PerimeterConsumption {
    id: number;
    name: string;
    code: string;
    cover_image: number;
    trajectories: PerimeterTrajectory[];
  }
  export interface SiteConsumption {
    multi_perimeter_id: number;
    name: string;
    code: string;
    perimeters: PerimeterConsumption[];
  }

  export interface SiteAssetConsumptionsChartData {
    [energy_type: number]: {
      asset_consumption: number;
      energy_color: string;
      energy_name: string;
      site_consumption: number;
      unit: IdNameMixin;
    };
  }

  export interface AssetsByYearAndNote {
    [year: string]: {
      [note: string]: number;
    };
  }

  export interface LifetimeVsTheorical {
    total_assets: number;
    assets_average_age: number;
    results: {
      [category: number]: {
        lifetime_less_than_theorical: number;
        lifetime_equals_theorical: number;
        lifetime_greater_than_theorical: number;
      };
    };
  }

  export interface BudgetReporting extends BudgetReportingDetails {
    investment_budget: BudgetReportingDetails;
    postponement_budget: BudgetReportingDetails;
    section?: BudgetReportingSection;
    postponed: boolean;
  }

  export interface BudgetReportingDetails {
    flash_budget: number;
    normal_budget: number;
    total_budget: number;
  }

  export interface BudgetReportingSection extends IdNameMixin {
    order: number;
    statuses: IdNameMixin[];
  }

  export interface EnvironmentalProfile {
    id: number;
    product_name: string;
    is_default: boolean;
    modified_on: string;
    reporting_organization: string;
    source: string;
    production_emissions: number;
    life_emissions: number;
    end_of_life_emissions: number;
    transport_emissions: number;
    maintenance_emissions: number;
    version: number;
    end_of_validity: string;
  }

  export interface SettingsMenuItem {
    id: string;
    label: string;
    value: boolean;
    requireEnergy?: boolean;
    requireAriba?: boolean;
  }

  export interface DecisionSettings {
    enable_consuming_features: boolean;
    enable_ppa_automatic: boolean;
    show_projects_tab: boolean;
    workflow_enabled: boolean;
  }

  export interface DecisionSettingsMenu {
    pluriannual_plan: SettingsMenuItem;
    technicals_lots: SettingsMenuItem;
    invest_history_N: SettingsMenuItem;
    invest_history_N1: SettingsMenuItem;
    validated_vs_engaged: SettingsMenuItem;
  }

  export interface EnergySettingsMenu {
    impact_CAPEX: SettingsMenuItem;
    energy_trajectory: SettingsMenuItem;
    kwh_consumption: SettingsMenuItem;
    carbon_trajectory: SettingsMenuItem;
    stranded_sites: SettingsMenuItem;
    avoided_ghg_emissions: SettingsMenuItem;
    taxonomy_aligned_green_CAPEX: SettingsMenuItem;
  }

  /*
   * models for site consumption (Stellantis)
   */

  export interface EnergyData {
    energy_consumption: {
      energy_type: {
        id: number;
        name: string;
        color: string;
        is_electricity: boolean;
      };
      consumptions: {
        [date: string]: {
          consumption_absolute: number;
          absolute_decarbonized_energy: number;
          absolute_renewable_energy: number;
        };
      };
    }[];
    generations: {
      energy_type: {
        id: number;
        name: string;
        color: string;
      };
      generate: {
        [date: string]: {
          consumption_absolute: number;
          absolute_decarbonized_energy: number;
          renewable_energy_absolute: number;
        };
      }[];
    }[];
    total_area: number;
  }

  export interface EnergyCarbonData {
    carbon_emissions: {
      energy_type: {
        id: number;
        name: string;
        color: string;
        is_electricity: boolean;
      };
      carbon_emission: {
        [date: string]: number;
      };
    }[];
    total_area: number;
  }

  export interface EnergyDataCorrected {
    raw_consumptions: {
      [date: string]: {
        [energyType: string]: number;
      };
    };
    corrected_consumptions: {
      [date: string]: {
        [energyType: string]: number;
      };
    };
    total_area: number;
  }

  export interface EnergyContract {
    id: number;
    name: string;
    contract_id: string;
    percentage_decarbonized_energy: string;
    percentage_renewable_energy: string;
    start_date: string;
    end_date: string;
    energy_type: number;
    emission_factor_provider: number;
  }

  export interface EnergyCounter extends IdNameMixin {
    id: number;
    energy_contracts: Pick<EnergyContract, "id" | "name" | "contract_id">[];
    unit: IdNameMixin;
    energy_type: string;
    uuid: string;
    generated_energy: boolean;
    source: string;
    perimeter: number;
  }

  export interface EnergyCounterCreation {
    name: string;
    source: string;
    perimeter_id: number;
    energy_type_id: number;
    contract_id: number;
    measurement_unit_id: number;
    generated_energy: boolean;
    start_date: Date;
    end_date?: Date;
  }

  export interface MeasurementUnit extends IdNameMixin {}

  export interface RatingReasonAssetsMap {
    [id: number]: RatingReasonAssets;
  }
  export interface RatingReasonAssets {
    id?: number;
    assets: {
      asset: number;
      year: number;
    }[];
    display_in_reports: boolean;
    name: string;
    order: number;
  }
  export interface Dimensions {
    width: number;
    height: number;
  }

  export interface CustomColDef extends ColDef {
    quickFilter?: boolean;
    altName?: string;
    booleanToNumberFilter?: boolean;
    availableFilterField?: string;
    allowNull?: boolean;
    alwaysPositive?: boolean;
  }

  export interface CustomColGroupDef extends ColGroupDef {
    children: (CustomColDef | CustomColGroupDef)[];
    filter?: string;
    altName?: string;
    booleanToNumberFilter?: boolean;
    notNull?: boolean;
    icons?: {
      [key: string]: ((...args: any[]) => any) | string;
    };
  }

  export interface QuickFilter {
    field: TableFilterColumns;
    name: string;
    numberFilter?: boolean;
    booleanToNumberFilter?: boolean;
    refData: {
      [key: string]: string;
    };
    isOther?: boolean;
    availableFilterField?: TableFilterColumns;
  }

  export type SpreadsheetType =
    | "investment"
    | "asset"
    | "initiative"
    | "site"
    | "ddr"
    | "group"
    | "user"
    | "investTag"
    | "actionTag"
    | "weighting";

  export interface AutoPPARow {
    label: string;
    site: number;
    perimeter: number;
    budget: number;
    year: number;
    priority: number;
    investment_status: number;
    asset_label: string;
    links: IdNameMixin[];
    asset: number;
    asset_name: string;
    quantity: number;
    replacement_asset_type: number;
    status: number;
    super_category: number;
  }

  export interface AssetTypeReplacementResponse {
    asset_types: AssetTypeReplacement[];
    all_choices: AssetTypeReplacementItem[];
  }

  export interface AssetTypeReplacement {
    asset_type: number;
    replace_by: AssetTypeReplacementItem;
    short_comment: string;
    long_comment: string;
    siblings: AssetTypeReplacementItem[];
    asset_type_object?: AssetType;
    sub_category_object?: SubCategory;
  }

  export interface AssetTypeReplacementItem extends IdNameMixin {
    price_sheet: number;
    min_price: number | null;
    max_price: number | null;
    average_price: number | null;
    asset_type_object?: AssetType;
    sub_category_object?: SubCategory;
  }

  export interface EmissionFactorResults {
    country: {
      emission_factor_providers: EmissionFactorProviderDef[];
      id: number;
      name: string;
      nb_sites: number;
    };
  }

  export interface EmissionFactorByCountry {
    energy_type: InitiativeEnergyType;
    results: EmissionFactorResults[];
  }

  export interface EmissionProviderPayload {
    sites: number[];
    scenario: number;
    provider: number;
  }

  export interface EmissionFactorProvider extends IdNameMixin {
    description?: string;
    location_based_emission_factors?: LocationBasedEmissionFactor[];
    market_based_emission_factors?: MarketBasedEmissionFactor[];
    synthese?: string;
    is_default: boolean;
    show_as_objective: boolean;
    based: string;
  }

  export interface EmissionFactorProviderScenario extends IdNameMixin {}

  export interface EmissionFactorProviderDef extends IdNameMixin {
    scenario: EmissionFactorProviderScenario;
  }

  export interface LocationBasedEmissionFactor extends IdNameMixin {
    color: string;
    countries: EmissionFactorDetailByCountry[];
  }

  export interface MarketBasedEmissionFactor extends IdNameMixin {
    emission_factors: EmissionByYear;
  }

  export interface EmissionFactorDetailByCountry extends IdNameMixin {
    emission_factors: EmissionByYear;
  }

  export interface EmissionFactorBySiteResponse {
    data: SiteEmissionFactors[];
    energy_type: InitiativeEnergyType;
  }

  export interface SiteEmissionFactors extends IdNameMixin {
    cluster: string;
    country: IdNameMixin;
    writeable: boolean;
    scenarios: ScenarioEmissionFactor[];
  }

  export interface ScenarioEmissionFactor extends IdNameMixin {
    provider: {
      id: number;
      name: string;
      is_default: boolean;
    };
  }

  export interface EmissionByYear {
    [key: string]: number;
  }

  export interface EmissionValueByCountryAndYear {
    country: IdNameMixin;
    data: EmissionValueByYear;
  }

  export interface EmissionValueByYear {
    [year: number]: number;
  }

  export interface TaxonomyObjectiveBudget {
    id: number;
    name: string;
    total_planned: number;
  }

  export interface RoadmapIndicatorBudget extends IdNameMixin {
    friendly_name: string;
    is_energy_related: boolean;
    is_energy_carbon: boolean;
    budget: number;
    investment_count: number;
  }

  export interface EnergyObjective {
    value: string;
    name: string;
    disabled?: boolean;
  }

  export interface AgGridBooleanFilterOption {
    name: string;
    value: boolean;
  }

  export interface ReportTemplate {
    id: number;
    label: string;
    mnemonic: string;
    report_type_ref: string;
    specific_data: any;
  }

  export interface ReportTemplateInputData {
    perimeter_id?: number;
    perimeters_id?: number[];
    buildings_id?: number[];
    start_year: number;
    end_year: number;
    perimeter_type_ids?: number[];
    decarbonized_scenario?: number;
    energy_provider?: number;
    energy_type_of_use?: string;
    carbon_provider?: number;
    carbon_type_of_use?: string;
    include_hypothesis?: Boolean;
    include_planned?: Boolean;
    energy_consumption_key?: ConsumptionType;
    carbon_consumption_key?: ConsumptionType;
    level_one_sub_perimeter_ids?: number[];
  }

  export interface ProviderPathway {
    provider: PathwayProvider;
    country: PathwayCountry;
    objectives: [];
  }

  export interface SitePathway {
    [perimeter_id: number]: {
      [year: number]: {
        area: number;
        kwh_objective: number;
        kwh_objective_ratio?: number;
        ghg_objective?: number;
        ghg_objective_ratio?: number;
      };
    };
  }

  export interface InvestmentPlan {
    id: number;
    year: number;
    closed: boolean;
    flash_global_budget: boolean;
  }

  export interface WorkflowStep {
    notification_tos: StepMember[];
    id: number;
    name: string;
    order: number;
    no_go_step: boolean;
    is_locked: boolean;
    is_ignored_by_default: boolean;
    can_change: boolean;
    all_sites_ack: boolean;
    managed_by: {
      id: number;
      members: StepMember[];
      name: string;
    };
  }

  export interface StepMember {
    first_name: string;
    last_name: string;
    role: string;
  }

  export interface WorkflowRound {
    id: number;
    name: string;
    owner_id: number;
    owner_name: string;
    created_on: string;
    steps: KPI.WorkflowStep[];
    project_code?: string;
    latest_comment?: {
      comment_by: string;
      comment_by_email: string;
      comment_on: string;
    } | null;
  }

  export interface WorkflowRoundCreation {
    round: KPI.WorkflowRound;
    notified: boolean;
  }
  export interface WorkflowRoundsListResponse {
    rounds_needing_action: KPI.WorkflowRound[];
    rounds_for_information: KPI.WorkflowRound[];
  }
  export interface WorkflowRoundHistory {
    id: number;
    name: string;
    running_sites_count: number;
    sites_count: number;
    proposed_amount: number;
    validated_amount: number;
    validation_date: Date | null;
    steps: KPI.WorkflowStep[];
    created_by: string;
    investment_ids: number[];
    created_on: string;
    site: string;
    waiting_group: string | null;
    latest_comment: { author: string | null; date: string | null } | null;
    project_codes: string[];
    project_code_investment_ids: number[];
    documents_count: number;
  }

  export interface WorkflowRoundStep {
    id: number;
    step: WorkflowStep;
    acked_on: Date | null;
    acked_by: string;
    acked_by_email: string;
  }

  export interface WorkflowRoundStepComment {
    id: number;
    step: number;
    comment_on: Date;
    updated_on: Date;
    comment_by: string;
    text: string;
    comment_by_id: number;
    workflow_round: KPI.WorkflowRound;
    // if true, mean I started the comment
    is_mine?: boolean;
  }

  export interface WorkflowRoundSite {
    site: WorkflowSite;
    steps: WorkflowRoundStep[];
    comments: WorkflowRoundStepComment[];
    proposed_amount: number;
    proposed_invest_ids: number[];
    validated_amount: number;
    closed_on: Date | null;
    closed_by: string;
    created_on: Date;
    stopped_on: Date | null;
    stopped_by_email: string;
    stopped_by_ack: boolean;
  }

  export interface WorkflowSiteStep {
    notification_tos: StepMember[];
    id: number; // step id
    acked: boolean;
    acked_on: Date | null;
    acked_by: string;
    acked_by_email: string;
    step: WorkflowStep;
    managed_by: {
      id: number;
      members: StepMember[];
      name: string;
    };
  }

  export interface WorkflowRoundResponse {
    sites: WorkflowSite[];
    active_step: WorkflowStep;
  }

  export interface WorkflowSite {
    id: number;
    name: string;
    validated_count: number;
    validated_amount: number;
    proposed_count: number;
    proposed_amount: number;
    proposed_invest_ids?: number[];
    closed_count: number;
    closed_amount: number;
    engaged_count: number;
    engaged_amount: number;
    prev_validated_count: number;
    prev_validated_amount: number;
    prev_engaged_count: number;
    prev_engaged_amount: number;
    stopped: boolean;
    steps: WorkflowSiteStep[];
    comments: WorkflowRoundStepComment[];
    proposed_by_tag: any;
    flash?: { count: number; budget: number; investment_ids: number[] };
  }

  export interface WorkflowStepAck {
    round: number;
    perimeter: number;
    step: number;
  }

  export interface WorkflowStopSite {
    round: number;
    perimeter: number;
  }

  export interface WorkflowCloseSite {
    round: number;
    perimeter: number;
  }

  export interface WorkflowStatus {
    ok: boolean;
  }

  export interface WorkflowStatusEx {
    ok: boolean;
    notified: boolean;
  }

  export interface WorkflowFlashSiteBudget {
    site: KPI.WorkflowSite;
    budget: number;
  }

  export interface WorkflowFlashBudget {
    global: boolean;
    global_budget: number;
    error: string;
    sites: WorkflowFlashSiteBudget[];
  }

  export interface WorkflowFlashSiteInvestments {
    site: KPI.WorkflowSite;
    request_amount: number;
    request_count: number;
    validated_amount: number;
    validated_count: number;
  }

  export interface WorkflowFlashGlobalInvestments {
    request_amount: number;
    request_count: number;
    validated_amount: number;
    validated_count: number;
  }

  export interface WorkflowFlashInvestments {
    global: boolean;
    global_investments: WorkflowFlashGlobalInvestments;
    error: string;
    sites: WorkflowFlashSiteInvestments[];
  }

  export interface WorkflowPerimeterPlan {
    id: number;
    name: string;
    validated_amount: number;
    validated_count: number;
    investment_ids?: number[];
    status: number;
    running_round: boolean;
    comments: KPI.WorkflowRoundStepComment[];
  }

  export interface SiteCards {
    title: string;
    value: string | number | string[];
    precision?: string;
    noDatas?: boolean;
    allowedNavigation?: boolean;
    navigateTo?: () => void;
    prefix?: string;
  }

  export interface PatchProviderEmissionFactorsOptions {
    energy_type: number;
    emission_factors: {
      year: number;
      factor: number;
    }[];
  }

  export enum ASSET_STEP {
    ACTIVE = 1,
    ARCHIVED = 2,
    FUTURE = 3
  }

  export type LeftRight = "left" | "right";

  export type ElementsChoiceMenuType = "ENERGY" | "ENERGY_COMPARISON" | "DECISION" | "SCENARII_INVEST_PLAN";

  export interface ElementsMenuItem {
    id: string;
    label: string;
    value: boolean;
    requireEnergy?: boolean;
    requireMultipleSites?: boolean;
    requireAriba?: boolean;
  }

  export interface ElementsChoiceMenu {
    impact_CAPEX?: ElementsMenuItem;
    technical_state?: ElementsMenuItem;
    energy_trajectory?: ElementsMenuItem;
    carbon_trajectory?: ElementsMenuItem;
    energy_carbon_trajectory?: ElementsMenuItem;
    stranded_sites?: ElementsMenuItem;
    financial_payback?: ElementsMenuItem;
    new_vs_current_initiatives?: ElementsMenuItem;
    pluriannual_plan?: ElementsMenuItem;
    technicals_lots?: ElementsMenuItem;
    taxonomy_CAPEX?: ElementsMenuItem;
    avoided_ghg_emissions?: ElementsMenuItem;
    taxonomy_aligned_green_CAPEX?: ElementsMenuItem;
    kwh_consumption?: SettingsMenuItem;
    invest_history_N?: SettingsMenuItem;
    invest_history_N1?: SettingsMenuItem;
    validated_vs_engaged?: SettingsMenuItem;
  }

  export interface ScenarioData {
    id: number;
    status: number;
    scenario_id: number;
    final_schedule: number;
    final_schedule_to: number;
    original_investment_id: number;
  }

  export interface YearConsumptionInput {
    perimeter_id: number;
    energy_type_id: number;
    consumption?: number;
    consumption_corrected_climate?: number;
    consumption_corrected_official?: number;
    year: number;
    comment: string;
  }

  export interface InvestmentMonitoring {
    results: {
      year: number;
      validated: {
        "amount": number;
        "investments_count": number;
        "investments_id": number[];
      };
      engaged: {
        "amount": number;
      }
    }[]
  }

  export interface StepDetails extends IdNameMixin {
    count: number;
    amount: number;
  }

  export interface AssetTypesTableItem extends IdNameMixin {
    sub_category: string;
    category: string;
    super_category: string;
    notation_weight: number;
    assets_count: number;
  }

  export type WorkflowClosureStatus =
    | "closed_and_validated"
    | "closed_but_partially_validated"
    | "closed_but_not_validated";

  export type TPreExportResp = {
    count: number;
    error_columns: string[];
    requested_columns: Map<string, string>;
    limitation: number;
    /** fastest, slowest estimation in order to download,  */
    estimations: number;
  };

  export type TExportPayload = {
    payload: TServerSideGetRowsRequest;
    columnDefs: ColDef<any, any>[];
  };

  export type TServerSideGetRowsRequest = IServerSideGetRowsRequest & {
    buildings: number[];
    scenario?: number;
    year?: number;
  };

  // PATHWWAY INTERFACE START

  export interface PathwayProvider {
    name: string;
    is_default: boolean;
  }

  export interface PathwayObjective {
    year: number;
    consumption: number;
    emission: number;
    declared: boolean;
  }

  export interface PathwayTypeOfUseObjectives {
    type_of_use: TypeOfUse;
    type_of_use_label: string;
    objectives: PathwayObjective[];
  }

  export interface PathwayCountry {
    name: string;
  }

  export interface PathwayCountryObjectives {
    country: PathwayCountry;
    type_of_use_objectives: PathwayTypeOfUseObjectives[];
  }

  export interface PathwayProviderObjectives {
    provider: PathwayProvider;
    country_objectives: PathwayCountryObjectives[];
  }

  export interface PathwaySite extends IdNameMixin {}

  export interface PathwayMonoPerimeter extends IdNameMixin {}

  export interface PathwayYearlyObjectives {
    [year: string]: PathwayObjective;
  }

  export interface MonositeObjectives {
    mono_perimeter: PathwayMonoPerimeter;
    objectives: PathwayYearlyObjectives;
  }

  export interface PathwaySiteObjectives {
    site: PathwaySite;
    monosite_objectives: MonositeObjectives[];
  }

  export interface ObjectivesPathway {
    provider_objectives: PathwayProviderObjectives;
    site_objectives: PathwaySiteObjectives[];
  }

  export interface SelectedToUPathway {
    name: string;
    emission: { [key: number]: number | null };
    consumption: { [key: number]: number | null };
  }

  export enum TheoreticalOrAuditFilter {
    THEORETICAL = "THEORETICAL",
    AUDIT = "AUDIT"
  }

  // PATHWAY INTERFACE END

  export interface FidjiActRowData {
    managed?: boolean;
    non_applicable?: boolean;
    non_applicable_at?: string;
    non_applicable_by?: string;
    already_done?: boolean;
    already_done_at?: string;
    already_done_by?: string;
    to_investigate?: boolean;
    to_investigate_at?: string;
    to_investigate_by?: string;
    comment?: string;
    comment_by?: string;
    comment_at?: string;
    section_setup_reference: string;
    subsection_setup_reference: string;
    type: "string" | "bool" | "ListAction";
    uuid: string;
    id?: number;
    value: string | boolean | FidjiActListActionItem[] | FidjiActObjectiveValue;
  }

  export interface FidjiActListActionItem {
    action_uuid: string;
    id: number;
    label: string;
    savings_start_at: string;
    status_setup_reference: string;
    cost: number | null;
  }

  export interface FidjiActObjectiveValue {
    consumption_diff: number;
    emission_diff: number;
    corrupt: boolean;
  }

  export interface FidjiActTransposedSite {
    label: string;
    building: string;
    id: number;
    uuid: string;
    site_id: number;
  }

  export interface FidjiActTransposedChild {
    column_name: string;
    subsection: string;
    friendly_name: string;
    code: string;
    cells: { [siteUuid: string]: FidjiActRowData };
  }

  export interface FidjiActTransposedSection {
    section: string;
    section_setup_reference: string;
    expanded: boolean;
    children: FidjiActTransposedChild[];
    review_counts: { [siteUuid: string]: { reviewed: number; total: number } };
  }

  export interface FidjiActTransposed {
    sites: FidjiActTransposedSite[];
    sections: FidjiActTransposedSection[];
    deactivated_action_types: string[];
    can_edit_priority: boolean;
    task_indicators: { [actionCode: string]: { [buildingId: string]: { count: number; emails: string[] } } };
    fiche_notice_initiative_type_ids: number[];
  }

  export interface FidjiActTask {
    id: number;
    label: string;
    assigned_email: string;
    state: number;
    state_display: string;
    deadline: string;
    section_reference: string;
    section_name: string;
    actions_concerned: string[];
    actions_concerned_names: string[];
    site_name: string;
    building_name: string;
    site_id: number | null;
    building_id: number | null;
    document_url: string | null;
    document_name: string | null;
    created_at: string;
  }

  export interface FidjiActFicheNoticeReferent {
    id: number;
    name: string;
    company: string;
    email: string;
    referent_type: 'ESG' | 'technique' | '';
    order: number;
  }

  export interface FidjiActFicheNotice {
    id: number | null;
    comment: string;
    resume: string;
    description: string;
    taxonomie: string;
    updated_at: string | null;
    referents: FidjiActFicheNoticeReferent[];
  }

  /**
   * Comité vert meeting report ("compte rendu") - see apps.meeting_report on the backend.
   * Simplified design: the whole report is one free-form rich-text document edited
   * with Tiptap. `content` is Tiptap's native JSON (editor.getJSON()) - a ProseMirror
   * document tree - stored and returned as-is, not HTML.
   */
  export interface TiptapMark {
    type: string;
    attrs?: Record<string, any>;
  }

  export interface TiptapNode {
    type: string;
    attrs?: Record<string, any>;
    content?: TiptapNode[];
    text?: string;
    marks?: TiptapMark[];
  }

  export interface TiptapDocument {
    type: 'doc';
    content: TiptapNode[];
  }

  /** Restricted UserSerializer(fields=("first_name","last_name","teammember")) shape. */
  export interface MeetingReportUser {
    first_name?: string;
    last_name?: string;
    teammember?: { id: number; name: string; first_name: string; last_name: string; email: string } | null;
  }

  export interface MeetingReport {
    id: number;
    perimeter: { id: number; name: string };
    /** {} when nobody has ever saved the report yet - frontend fills in the
     * default section-headings skeleton in that case. */
    content: TiptapDocument | Record<string, never>;
    updated_by: MeetingReportUser | null;
    updated_at: string | null;
    created_by: number | null;
  }

  export type MeetingReportIndicatorCategory = 'energy_carbon' | 'biodiversity' | 'water' | 'waste';

  /** Catalog entry (reference data, no per-perimeter value) shown as a prefix
   * under "4. Indicateurs de suivi" when a report is first opened. */
  export interface MeetingReportIndicator {
    id: number;
    name: string;
    category: MeetingReportIndicatorCategory;
    order: number;
  }

  export type SettingMap = {
    datetime: "string";
    decimal: number;
    email: string;
    bool: boolean;
    float: number;
    int: number;
    json: object;
    date: string;
    string: string;
    text: string;
    time: string;
    url: string;
  };

  export type Setting = {
    name: string;
    value_type: string;
    value: any;
  };

  // Maintenance interfaces
  export interface AssetMaintenance {
    id: number;
    asset: number;
    has_contract: boolean;
    company: string;
    contract_deadline: string;
    level: number;
    responsibility: string;
    manufacturer_control_frequency: string;
    control_frequency: string;
    last_control_date: string;
    equipment_evaluation: string;
    last_evaluation: string;
    evaluated_by: string;
    evaluation_date: string;
    attached_to_equipment: boolean;
    comment: string;
  }

  export interface AssetMaintenanceCreatePayload {
    asset: number;
    has_contract: boolean;
    company: string;
    contract_deadline: string;
    level: number;
    responsibility: string;
    manufacturer_control_frequency: string;
    control_frequency: string;
    last_control_date: string;
    equipment_evaluation: string;
    last_evaluation: string;
    evaluated_by: string;
    evaluation_date: string;
    attached_to_equipment: boolean;
    comment: string;
  }

  export interface AssetMaintenanceUpdatePayload {
    asset: number;
    has_contract: boolean;
    company: string;
    contract_deadline: string;
    level: number;
    responsibility: string;
    manufacturer_control_frequency: string;
    control_frequency: string;
    last_control_date: string;
    equipment_evaluation: string;
    last_evaluation: string;
    evaluated_by: string;
    evaluation_date: string;
    attached_to_equipment: boolean;
    comment: string;
  }

  // Compliance interfaces
  export interface AssetCompliance {
    id: number;
    asset: number;
    control_required: boolean;
    control_frequency: string;
    control_description: string;
    last_control_date: string;
    environmental_compliance: boolean;
    compliance_type?: string;
    certification_date?: string;
    expiry_date?: string;
    certificate_number?: string;
    issuing_authority?: string;
    status?: string;
  }

  export interface AssetComplianceCreatePayload {
    asset: number;
    control_required: boolean;
    control_frequency: string;
    control_description: string;
    last_control_date: string;
    environmental_compliance: boolean;
    compliance_type?: string;
    certification_date?: string;
    expiry_date?: string;
    certificate_number?: string;
    issuing_authority?: string;
    status?: string;
  }

  export interface AssetComplianceUpdatePayload {
    asset: number;
    control_required: boolean;
    control_frequency: string;
    control_description: string;
    last_control_date: string;
    environmental_compliance: boolean;
    compliance_type?: string;
    certification_date?: string;
    expiry_date?: string;
    certificate_number?: string;
    issuing_authority?: string;
    status?: string;
  }

  // Reservations interfaces
  export interface Reservation {
    id: number;
    compliance?: number;
    control_office_report?: number;
    linked_equipment?: number;
    perimeter?: number;
    issue_date?: string;
    label: string;
    precision?: string;
    comment?: string;
    source?: string;
    criticality?: string;
    status?: string;
    responsible?: string;
    closing_date?: string;
    created_at?: string;
    updated_at?: string;
  }

  export interface ReservationCreatePayload {
    compliance?: number;
    control_office_report?: number;
    perimeter?: number;
    label?: string;
    issue_date?: string;
    precision?: string;
    comment?: string;
    source?: string;
    criticality?: string;
    status?: string;
    responsible?: string;
    closing_date?: string;
  }

  export interface ReservationUpdatePayload {
    compliance?: number;
    control_office_report?: number;
    perimeter?: number;
    label?: string;
    issue_date?: string;
    precision?: string;
    comment?: string;
    source?: string;
    criticality?: string;
    status?: string;
    responsible?: string;
    closing_date?: string;
  }

  export interface ReservationsBulkCreatePayload {
    asset_id?: number | null;
    perimeter_id?: number | null;
    control_office_report_id?: number | null;
    reservations: ReservationCreatePayload[];
  }

  export interface ReservationsBulkCreateResponse {
    success: boolean;
    created_count: number;
    reservations: Reservation[];
  }

  export interface ControlOfficeReport {
    id: number;
    label: string;
    date: string;
    url?: string;
    linked_equipment: number[];
    perimeter?: number;
    closing_date?: string;
    comment?: string;
    active_reservations_count?: number;
    critical_reservations_count?: number;
    total_reservations_count?: number;
    created_at?: string;
    updated_at?: string;
  }

  export interface ControlOfficeReportCreatePayload {
    label: string;
    date: string;
    url?: string;
    perimeter?: number;
    linked_equipment?: number[];
    closing_date?: string;
    comment?: string;
  }

  export interface ControlOfficeReportUpdatePayload {
    label?: string;
    date?: string;
    url?: string;
    perimeter?: number;
    linked_equipment?: number[];
    closing_date?: string;
    comment?: string;
  }

  export interface ReportReservationsStats {
    total: number;
    active: number;
    critical: number;
    closed: number;
    abandoned: number;
  }

  export interface PlatformInfo {
    version: string | null;
    release_date: string | null;
  }
}
export interface QuotePartCreatePayload {
  name?: string;
  sponsors?: QuotePartSponsor[];
  is_other?: boolean;
  perimeters?: number[];
  // list_parent_perimeter_types: number[];
  // list_child_perimeter_types: number[];
  refacturable?: boolean;
  site_id?: number;
  color_code?: string;
}
export interface QuotePartUpdatePayload extends QuotePartCreatePayload {
  id?: number;
}
export interface QuotePartSponsor {
  id: number;
  sponsor_id: number;
  // percentage can come from Decimal, so represent a string
  percentage: number | string;
  property_code?: string;
  property_code_label?: string;
  quote_part_id: number;
}

/**
 * add name field
 */
export interface QuotePartSponsorshipData extends QuotePartSponsor {
  name?: string;
}
export interface QuotePartItem {
  name: string;
  sponsors: QuotePartSponsorshipData[];
  // list_parent_perimeter_types: KPI.PerimeterType[];
  // list_child_perimeter_types: KPI.PerimeterType[];
  refacturable?: boolean;
  site_id: number;
  can_edit_quote_part_site: boolean;
  id: number;
  color_code?: string;
  perimeters: number[];
  investments: number[];
  is_other?: boolean;
}
export interface QuotePartPermission {
  can_edit_quote_part_site: boolean;
}
export interface EnableValue {
  enable: boolean;
}
export interface PerimeterTypes {
  perimeter_type_ids: number[];
}
export interface AssociatedPerimeters {
  perimeter_ids: number[];
}
export interface QuotePartIds {
  quote_part: number[];
}
export interface QuotePartCreateResponse {}
export interface QuotePartDeleteResponse {}
export interface QuotePartUpdateResponse extends QuotePartItem {}

/**
 * Roadmap Inventaire
 */
type ChapterInventaire = {
  subject: string;
  label: string;
  id: number;
  active: boolean;
  is_creation_perimeter: boolean;
  items_perimeter_types: number[];
  themes?: { id: number; name: string }[];
};

export type RoadmapInventaire = {
  id: number;
  level: number;
  name: string;
  order: number;
  perimeter_type_class: KPI.PerimeterTypeClass;
  chapters: ChapterInventaire[];
  children: RoadmapInventaire[];
};

export type spendingCategoriesSection = {
  enabled: boolean;
  selectedCategory: KPI.InvestmentCategory;
  selectedCategoryId: number;
  categories: KPI.InvestmentCategory[];
};
